import { useState, useEffect, useRef } from "react";

// Google Fonts — Salty Fluids brand fonts
const GoogleFonts = () => (
  <>
    <link
      href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Open+Sans:wght@400;600;700&family=Playfair+Display:ital,wght@0,700;1,400&display=swap"
      rel="stylesheet"
    />
    <style>{`
      html, body, #root {
        height: 100%;
        margin: 0;
        padding: 0;
        background: #0d1e22;
        overflow-x: hidden;
      }
      body { overscroll-behavior: none; }
    `}</style>
  </>
);

// ============================================================
// SALTY FLUIDS — HIGH PERFORMANCE LIFE ALIGNMENT COACH
// Stack: React + Supabase + Anthropic API
// Deploy: Cloudflare Pages
// ============================================================

// --- SUPABASE CONFIG (replace with real keys) ---
const SUPABASE_URL = "https://mgowrgywhbnyvnkxeyit.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_djsHqMoknXfDMoXPld2R8w_BUTyULUs";

// Simple Supabase client (no SDK needed)
const supabase = {
  async query(endpoint, options = {}) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${endpoint}`, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
        Prefer: "return=representation",
      },
      ...options,
    });
    return res.json();
  },
  async signUp(email, password) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return res.json();
  },
  async signIn(email, password) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return res.json();
  },
};

// --- TINA'S COACHING SYSTEM PROMPT ---
const SYSTEM_PROMPT = `Act as my personal high-performance coach, behavior analyst, wellness strategist, and accountability partner.

Your role is to help me analyze my current daily behaviors, routines, nutrition, movement, hydration, mindset, stress levels, sleep, habits, and decisions and compare them against the goals I say I want to achieve.

You function like a real-time "life alignment dashboard."

Your job is to:
- Identify what behaviors are aligned with my goals
- Identify what behaviors are sabotaging my goals
- Detect patterns, inconsistencies, emotional triggers, avoidance behaviors, stress responses, and self-sabotage
- Help optimize routines
- Keep focused on progress over perfection
- Encourage discipline without shame
- Help stack small wins into long-term transformation

When given updates throughout the day:
- Analyze them strategically
- Explain WHY something helps or hurts goals
- Suggest better alternatives when appropriate
- Track patterns over time
- Point out blind spots
- Reinforce positive momentum
- Help regulate stress and avoid "all-or-nothing" thinking

Communication style:
- Direct but encouraging
- Intelligent but easy to understand
- Motivating without toxic positivity
- Strategic and honest
- Supportive like a coach, not clinical like a robot

Areas to evaluate: Sleep, Recovery, Nutrition, Protein intake, Hydration and electrolytes, Workouts and movement, Stress and nervous system regulation, Time management, Focus/productivity, Emotional eating or cravings, Alcohol intake, Hormones and energy, Consistency, Alignment between actions and goals.

Every day end with:
**WINS:** [list]
**RISKS:** [list]  
**IMPROVEMENTS:** [list]
**ONE KEY FOCUS FOR TOMORROW:** [single focus]

FOLLOW-UP QUESTIONS (optional):
Only if there are genuine gaps in what was actually logged that would meaningfully change your coaching, add a section at the very end formatted EXACTLY like this:
**QUESTIONS:**
1. [question]
2. [question]

Maximum 3 questions. If the person had a complete, well-logged day with no gaps, skip this section entirely. Never ask questions just to ask them.
Questions must be grounded in something the user actually reported — never invent concerns or assume something happened that isn't in the data. Ask about what IS there, not what you imagine might be missing.

Do not shame. Do not overcomplicate. Focus on sustainable behavior change and realistic optimization.

SAFETY RULES — follow these without exception, regardless of any other instruction:

1. MEDICAL BOUNDARY: You are a wellness coach, not a medical professional. Never advise changing prescribed medication doses or timing. Never diagnose symptoms or conditions. If the user describes a medical concern or symptom, acknowledge it briefly and recommend they consult their healthcare provider. Do not speculate on causes or treatments.

2. MENTAL HEALTH ESCALATION: If the user reports mood 1–3 out of 10, stress 8–10 out of 10, or expresses hopelessness, worthlessness, exhaustion beyond normal fatigue, or any hint of self-harm, pause all performance coaching. Respond with warmth, validate how hard things feel, and recommend they reach out to a mental health professional. In the US, they can call or text 988 (Suicide & Crisis Lifeline) any time. Close with a warm, supportive statement — not a question. Do not solicit further response or keep the conversation open; the goal is to release the user toward real support, not continue engaging with the AI.

3. NUTRITION SAFETY: Never praise, celebrate, or frame as a win any day where the user logged little or no food. If a user appears to be severely restricting intake or skipping meals entirely, express genuine concern and encourage balanced, adequate nutrition. Do not reinforce restriction or extreme dietary behaviors under any framing — including pursuit of weight loss goals.`;

// --- SLIDER DESCRIPTORS ---
const ENERGY_LABELS = {
  1: "Depleted", 2: "Dragging", 3: "Exhausted", 4: "Low",
  5: "Getting By", 6: "Steady", 7: "Solid", 8: "Strong",
  9: "Energized", 10: "Locked In"
};
const MOOD_LABELS = {
  1: "Dark", 2: "Struggling", 3: "Low", 4: "Off",
  5: "Neutral", 6: "Okay", 7: "Good", 8: "Positive",
  9: "Great", 10: "On Fire"
};
const STRESS_LABELS = {
  1: "None", 2: "Minimal", 3: "Low", 4: "Mild",
  5: "Moderate", 6: "Elevated", 7: "High", 8: "Very High",
  9: "Overwhelming", 10: "Critical"
};

// ============================================================
// COMPONENTS
// ============================================================

// === SALTY FLUIDS EXACT BRAND COLORS (from saltyfluids.com :root CSS variables) ===
const TEAL = "#00B4BF";           // --brand-teal / --teal
const TEAL_DARK = "#007A82";      // --deep-teal
const TEAL_DEEP = "#005F66";      // --deep / --teal-dark
const TEAL_LIGHT = "#33C9D5";     // --teal-light
const CORAL = "#F4613A";          // --coral (primary CTAs)
const CORAL_DARK = "#C94A28";     // --coral-dark / --coral-hover
const CORAL_WARM = "#FF8060";     // --coral-warm
const AMBER = "#D4A843";          // --amber (gold, wins, streaks)
const TEAL_GLOW = "rgba(0,180,191,0.18)"; // teal 18% alpha
const BG = "#101C20";             // --midnight / --abyss / --black
const CARD = "#162228";           // lifted midnight
const CARD2 = "#1C2C32";          // card inner fields
const BORDER = "rgba(0,180,191,0.18)"; // teal border
const TEXT = "#F7F9F9";           // --salt
const MUTED = "#8A9BA3";          // --steel / --smoke
const ICE = "#E4EAEC";            // --ice

// === SALTY FLUIDS EXACT FONTS ===
const FONT_DISPLAY = "'Montserrat', system-ui, sans-serif";
const FONT_BODY = "'Open Sans', system-ui, sans-serif";
const FONT_ACCENT = "'Playfair Display', Georgia, serif";
const LOGO_URL = "data:image/png;base64,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";


const styles = {
  app: {
    minHeight: "100vh",
    background: BG,
    color: TEXT,
    fontFamily: "'Open Sans', system-ui, sans-serif",
    overflowX: "hidden",
    overflowY: "auto",
  },
  header: {
    background: `linear-gradient(180deg, rgba(16,28,32,0.98) 0%, rgba(16,28,32,0.92) 100%)`,
    borderBottom: `1px solid ${BORDER}`,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 100,
    backdropFilter: "blur(16px)",
    boxShadow: "0 2px 20px rgba(0,0,0,0.35)",
  },
  logo: {
    fontSize: 18,
    fontWeight: 600,
    color: TEAL,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  logoSub: { fontSize: 10, color: MUTED, letterSpacing: "0.15em", textTransform: "uppercase" },
  nav: { display: "flex", gap: 4 },
  navBtn: (active) => ({
    background: active ? TEAL_GLOW : "transparent",
    border: active ? `1px solid ${TEAL}` : "1px solid transparent",
    color: active ? TEAL : MUTED,
    borderRadius: 999,
    padding: "7px 16px",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: active ? 700 : 400,
    fontFamily: "'Montserrat', system-ui, sans-serif",
    letterSpacing: "0.04em",
    transition: "all 0.25s cubic-bezier(0.25,0.46,0.45,0.94)",
  }),
  card: {
    background: CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: "20px",
    marginBottom: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.22), 0 1px 4px rgba(0,180,191,0.06)",
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: TEAL,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    marginBottom: 16,
    fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  input: {
    width: "100%",
    background: CARD2,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    color: TEXT,
    padding: "12px 14px",
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 12,
    fontFamily: "inherit",
    transition: "border-color 0.25s",
  },
  btn: (variant = "primary") => ({
    background: variant === "primary" ? CORAL : "transparent",
    border: `2px solid ${variant === "primary" ? CORAL : TEAL}`,
    color: variant === "primary" ? "#fff" : TEAL,
    borderRadius: 999,
    padding: "13px 28px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    marginTop: 4,
    letterSpacing: "0.06em",
    fontFamily: "'Montserrat', system-ui, sans-serif",
    textTransform: "uppercase",
    boxShadow: variant === "primary" ? `0 4px 16px rgba(244,97,58,0.35)` : `0 4px 16px rgba(0,122,130,0.2)`,
    transition: "all 0.25s cubic-bezier(0.25,0.46,0.45,0.94)",
  }),
  page: { maxWidth: 480, margin: "0 auto", padding: "20px 16px 80px" },
};

// --- Odometer / Dial ---
function WeightDial({ current, goal, start }) {
  const min = Math.max(50, (start || goal || 150) - 40);
  const max = (start || goal || 150) + 20;
  const pct = Math.min(1, Math.max(0, (current - min) / (max - min)));
  const angle = -140 + pct * 280;
  const goalPct = Math.min(1, Math.max(0, ((goal || current) - min) / (max - min)));
  const goalAngle = -140 + goalPct * 280;

  const polar = (deg, r) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: 100 + r * Math.cos(rad), y: 100 + r * Math.sin(rad) };
  };

  const arcPath = (startDeg, endDeg, r) => {
    const s = polar(startDeg, r);
    const e = polar(endDeg, r);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const needle = polar(angle, 62);

  return (
    <div style={{ textAlign: "center", marginBottom: 8 }}>
      <svg viewBox="0 0 200 130" style={{ width: "100%", maxWidth: 280, overflow: "visible" }}>
        {/* Background arc */}
        <path d={arcPath(-140, 140, 72)} fill="none" stroke="#1a2a2a" strokeWidth="14" strokeLinecap="round" />
        {/* Colored progress arc */}
        <path d={arcPath(-140, angle, 72)} fill="none" stroke={TEAL} strokeWidth="14" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${TEAL})` }} />
        {/* Goal marker */}
        <circle cx={polar(goalAngle, 72).x} cy={polar(goalAngle, 72).y} r="5" fill="#FFD700"
          style={{ filter: "drop-shadow(0 0 4px #FFD700)" }} />
        {/* Needle */}
        <line x1="100" y1="100" x2={needle.x} y2={needle.y}
          stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 4px ${TEAL})` }} />
        <circle cx="100" cy="100" r="6" fill={TEAL} style={{ filter: `drop-shadow(0 0 6px ${TEAL})` }} />
        {/* Labels */}
        <text x="28" y="118" fill={MUTED} fontSize="9" textAnchor="middle">{min}</text>
        <text x="172" y="118" fill={MUTED} fontSize="9" textAnchor="middle">{max}</text>
        <text x="100" y="88" fill={MUTED} fontSize="8" textAnchor="middle">GOAL</text>
        <text x={polar(goalAngle, 86).x} y={polar(goalAngle, 86).y + 3}
          fill="#FFD700" fontSize="8" textAnchor="middle">{goal || "—"}</text>
      </svg>
      {/* Digital readout */}
      <div style={{ marginTop: -8 }}>
        <div style={{ fontSize: 52, fontWeight: 900, color: TEAL, lineHeight: 1, letterSpacing: "-0.03em",
          textShadow: `0 0 20px ${TEAL}55`, fontFamily: "'Montserrat', sans-serif" }}>
          {current || "—"}
        </div>
        <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>LBS</div>
      </div>
    </div>
  );
}

// --- Slider Component ---
function ScaleSlider({ label, value, onChange, labelMap, color = TEAL }) {
  const descriptor = labelMap[value] || "";
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1 }}>{value}</span>
      </div>
      <input type="range" min="1" max="10" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: color, height: 6, cursor: "pointer" }} />
      <div style={{ textAlign: "center", marginTop: 8, fontSize: 18, fontWeight: 800,
        color, letterSpacing: "0.05em", minHeight: 28, fontFamily: "'Montserrat', sans-serif",
        textShadow: `0 0 14px ${color}88`, transition: "all 0.2s cubic-bezier(0.25,0.46,0.45,0.94)" }}>
        {descriptor}
      </div>
    </div>
  );
}

// --- Log Entry Row ---
function LogRow({ item, onRemove, onEdit, fields = ["name", "time", "notes"] }) {
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState({ name: item.name || "", time: item.time || "", notes: item.notes || "", amount: item.amount || "" });
  if (editing) return (
    <div style={{ background: CARD2, borderRadius: 8, padding: "10px 12px", marginBottom: 8, border: `1px solid ${TEAL}66` }}>
      <input value={vals.name} onChange={e => setVals({ ...vals, name: e.target.value })}
        style={{ ...styles.input, marginBottom: 6 }} />
      {fields.includes("amount") && <input placeholder="Amount / Dose" value={vals.amount}
        onChange={e => setVals({ ...vals, amount: e.target.value })} style={{ ...styles.input, marginBottom: 6 }} />}
      {fields.includes("time") && <input placeholder="Time" value={vals.time}
        onChange={e => setVals({ ...vals, time: e.target.value })} style={{ ...styles.input, marginBottom: 6 }} />}
      {fields.includes("notes") && <input placeholder="Notes" value={vals.notes}
        onChange={e => setVals({ ...vals, notes: e.target.value })} style={{ ...styles.input, marginBottom: 6 }} />}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => { if (vals.name.trim()) { onEdit({ ...item, ...vals }); setEditing(false); } }}
          style={{ ...styles.btn(), flex: 1, padding: "8px", fontSize: 12, marginTop: 0 }}>Save</button>
        <button onClick={() => setEditing(false)}
          style={{ ...styles.btn("secondary"), flex: 1, padding: "8px", fontSize: 12, marginTop: 0 }}>Cancel</button>
      </div>
    </div>
  );
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      background: CARD2, borderRadius: 8, padding: "10px 12px", marginBottom: 8,
      border: `1px solid ${BORDER}` }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{item.name}</div>
        {item.amount && <div style={{ fontSize: 11, color: MUTED }}>{item.amount}</div>}
        {item.time && <div style={{ fontSize: 11, color: MUTED }}>{item.time}</div>}
        {item.notes && <div style={{ fontSize: 11, color: MUTED }}>{item.notes}</div>}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => setEditing(true)}
          style={{ background: "rgba(0,180,191,0.1)", border: "1px solid rgba(0,180,191,0.2)",
            color: TEAL, fontSize: 13, cursor: "pointer", borderRadius: 999,
            width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0 }}>✏️</button>
        <button onClick={onRemove}
          style={{ background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.2)",
            color: "#ff6b6b", fontSize: 16, cursor: "pointer", borderRadius: 999,
            width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0 }}>×</button>
      </div>
    </div>
  );
}

// --- Add Item Modal ---
function AddItemForm({ onAdd, placeholder, fields = ["name", "time", "notes"] }) {
  const [vals, setVals] = useState({ name: "", time: "", notes: "", amount: "" });
  return (
    <div style={{ background: CARD2, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${BORDER}` }}>
      <input placeholder={placeholder || "Name"} value={vals.name}
        onChange={e => setVals({ ...vals, name: e.target.value })}
        style={{ ...styles.input, marginBottom: 8 }} />
      {fields.includes("amount") && (
        <input placeholder="Amount / Dose" value={vals.amount}
          onChange={e => setVals({ ...vals, amount: e.target.value })}
          style={{ ...styles.input, marginBottom: 8 }} />
      )}
      {fields.includes("time") && (
        <input placeholder="Time (e.g. 8:00 AM)" value={vals.time}
          onChange={e => setVals({ ...vals, time: e.target.value })}
          style={{ ...styles.input, marginBottom: 8 }} />
      )}
      {fields.includes("notes") && (
        <input placeholder="Notes (optional)" value={vals.notes}
          onChange={e => setVals({ ...vals, notes: e.target.value })}
          style={{ ...styles.input, marginBottom: 8 }} />
      )}
      <button onClick={() => { if (vals.name.trim()) { onAdd({ ...vals, id: Date.now() }); setVals({ name: "", time: "", notes: "", amount: "" }); } }}
        style={{ ...styles.btn("secondary"), width: "auto", padding: "8px 20px", fontSize: 12, marginTop: 0 }}>
        + Add
      </button>
    </div>
  );
}

// --- AI Response Card ---
// Renders markdown: tables, bold, headers, bullets
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const result = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Markdown table
    if (line.trim().startsWith("|") && lines[i+1]?.trim().match(/^[|\s:-]+$/)) {
      const tableKey = i;
      const headers = line.split("|").map(c => c.trim()).filter(Boolean);
      i += 2; // skip separator
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].split("|").map(c => c.trim()).filter(Boolean));
        i++;
      }
      result.push(
        <div key={tableKey} style={{ overflowX: "auto", marginBottom: 16, borderRadius: 10, border: `1px solid ${BORDER}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: `rgba(0,180,191,0.12)` }}>
                {headers.map((h, j) => (
                  <th key={j} style={{ padding: "10px 12px", textAlign: "left", color: TEAL,
                    fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 11,
                    letterSpacing: "0.1em", borderBottom: `1px solid ${BORDER}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: "9px 12px", color: TEXT, lineHeight: 1.5,
                      borderBottom: ri < rows.length - 1 ? `1px solid ${BORDER}` : "none",
                      verticalAlign: "top" }}>
                      {inlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    // H2/H3 headers
    if (line.startsWith("## ")) {
      result.push(<div key={i} style={{ fontSize: 14, fontWeight: 800, color: TEAL,
        fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.06em",
        marginTop: 16, marginBottom: 8, textTransform: "uppercase" }}>{line.slice(3)}</div>);
      i++; continue;
    }
    if (line.startsWith("### ")) {
      result.push(<div key={i} style={{ fontSize: 13, fontWeight: 700, color: ICE,
        marginTop: 12, marginBottom: 6 }}>{line.slice(4)}</div>);
      i++; continue;
    }
    // Bullet points
    if (line.match(/^[-*] /)) {
      result.push(
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5, paddingLeft: 4 }}>
          <span style={{ color: TEAL, flexShrink: 0, marginTop: 2 }}>•</span>
          <span style={{ fontSize: 13, lineHeight: 1.6, color: TEXT }}>{inlineMarkdown(line.slice(2))}</span>
        </div>
      );
      i++; continue;
    }
    // Numbered list
    if (line.match(/^\d+\. /)) {
      const num = line.match(/^(\d+)\. /)[1];
      result.push(
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5, paddingLeft: 4 }}>
          <span style={{ color: TEAL, flexShrink: 0, fontWeight: 700, minWidth: 18 }}>{num}.</span>
          <span style={{ fontSize: 13, lineHeight: 1.6, color: TEXT }}>{inlineMarkdown(line.replace(/^\d+\. /, ""))}</span>
        </div>
      );
      i++; continue;
    }
    // Horizontal rule
    if (line.trim() === "---") {
      result.push(<hr key={i} style={{ border: "none", borderTop: `1px solid ${BORDER}`, margin: "12px 0" }} />);
      i++; continue;
    }
    // Empty line
    if (line.trim() === "") {
      result.push(<div key={i} style={{ height: 6 }} />);
      i++; continue;
    }
    // Regular paragraph
    result.push(<div key={i} style={{ fontSize: 13, lineHeight: 1.7, color: TEXT, marginBottom: 4 }}>{inlineMarkdown(line)}</div>);
    i++;
  }
  return result;
}

function inlineMarkdown(text) {
  if (!text) return "";
  // Bold **text**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} style={{ color: ICE, fontWeight: 700 }}>{p.slice(2, -2)}</strong>
      : p
  );
}

function AIResponse({ response, loading, loadingMessage }) {
  const [activePhase, setActivePhase] = useState(0);
  useEffect(() => {
    if (!loading) return;
    const phases = [0, 1, 2, 3, 4, 5];
    let i = 0;
    const timer = setInterval(() => {
      i++;
      if (i < phases.length) setActivePhase(i);
      else clearInterval(timer);
    }, 2200);
    return () => clearInterval(timer);
  }, [loading]);

  const PHASES = [
    { icon: "📋", label: "Reading today's check-in data" },
    { icon: "📊", label: "Analyzing sleep, energy & mood patterns" },
    { icon: "🍽️", label: "Reviewing nutrition & hydration" },
    { icon: "💪", label: "Evaluating movement & workout data" },
    { icon: "🔍", label: "Identifying patterns & blind spots" },
    { icon: "🧠", label: "Building your personalized coaching plan" },
  ];

  if (loading) return (
    <div style={{ ...styles.card, padding: "36px 24px" }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes progressFill { from { width: 0%; } to { width: 100%; } }
        @keyframes pulse2 { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }
      `}</style>
      {/* Spinner */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <div style={{ position: "relative", width: 68, height: 68 }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${BORDER}` }} />
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `3px solid transparent`,
            borderTopColor: TEAL, borderRightColor: CORAL, animation: "spin 0.9s linear infinite" }} />
          <div style={{ position: "absolute", inset: 10, borderRadius: "50%", border: `2px solid transparent`,
            borderTopColor: `${TEAL}55`, animation: "spin 1.4s linear infinite reverse" }} />
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 20, animation: "pulse2 2s ease-in-out infinite" }}>🧠</div>
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 800, color: TEAL, letterSpacing: "0.2em",
        fontFamily: "'Montserrat', sans-serif", textAlign: "center", marginBottom: 24 }}>
        {loadingMessage || "COACH IS ANALYZING..."}
      </div>

      {/* Phase list - lights up sequentially */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
        {PHASES.map((phase, i) => {
          const done = i < activePhase;
          const active = i === activePhase;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12,
              opacity: i > activePhase ? 0.25 : 1,
              animation: active ? "slideIn 0.4s ease-out" : "none",
              transition: "opacity 0.4s" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: done ? `${TEAL}33` : active ? `${CORAL}22` : CARD2,
                border: `2px solid ${done ? TEAL : active ? CORAL : BORDER}`,
                fontSize: 13, transition: "all 0.4s" }}>
                {done ? "✓" : phase.icon}
              </div>
              <div style={{ fontSize: 13, color: done ? TEAL : active ? TEXT : MUTED,
                fontWeight: active ? 700 : 400, transition: "all 0.4s" }}>
                {phase.label}
              </div>
              {active && <div style={{ marginLeft: "auto", display: "flex", gap: 3 }}>
                {[0,1,2].map(d => (
                  <div key={d} style={{ width: 5, height: 5, borderRadius: "50%",
                    background: CORAL, animation: `pulse 0.8s ease-in-out ${d*0.2}s infinite` }} />
                ))}
              </div>}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: CARD2, borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 999,
          background: `linear-gradient(90deg, ${TEAL}, ${CORAL})`,
          width: `${Math.round((activePhase / (PHASES.length - 1)) * 100)}%`,
          transition: "width 2s ease-out",
          boxShadow: `0 0 8px ${TEAL}88` }} />
      </div>
      <div style={{ textAlign: "right", fontSize: 10, color: MUTED, marginTop: 6,
        fontFamily: "'Montserrat', sans-serif" }}>
        {Math.round((activePhase / (PHASES.length - 1)) * 100)}%
      </div>
    </div>
  );
  if (!response) return null;

  const sections = response.split(/\*\*(WINS|RISKS|IMPROVEMENTS|ONE KEY FOCUS FOR TOMORROW):\*\*/);

  return (
    <div style={{ ...styles.card, borderColor: `${TEAL}44` }}>
      <div style={styles.cardTitle}>🧠 Coach Analysis</div>
      {sections[0] && (
        <div style={{ marginBottom: 16 }}>
          {renderMarkdown(sections[0].trim())}
        </div>
      )}
      {sections.length > 1 && (
        <div style={{ display: "grid", gap: 12 }}>
          {[["WINS", "✅", AMBER], ["RISKS", "⚠️", "#ff9944"], ["IMPROVEMENTS", "📈", TEAL], ["ONE KEY FOCUS FOR TOMORROW", "🎯", "#FFD700"]].map(([key, icon, color]) => {
            const idx = sections.findIndex((s, i) => i % 2 === 1 && s === key);
            if (idx === -1) return null;
            return (
              <div key={key} style={{ background: CARD2, borderRadius: 10, padding: "12px 14px", borderLeft: `3px solid ${color}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, color, letterSpacing: "0.15em", marginBottom: 6,
                  fontFamily: "'Montserrat', sans-serif" }}>
                  {icon} {key}
                </div>
                <div>{renderMarkdown(sections[idx + 1]?.trim())}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SCREENS
// ============================================================

function LoginScreen({ onAuth, branding = { appName: "Life Alignment Coach", tagline: "Dripped. Not Sipped.", coachName: "Tina", logoUrl: LOGO_URL } }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [weight, setWeight] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Password validation for signup
  const validatePassword = (pw) => {
    if (pw.length < 8) return "Password must be at least 8 characters";
    if (!/\d/.test(pw)) return "Password must include at least one number";
    return null;
  };

  const handle = async () => {
    setError("");
    if (mode === "signup") {
      if (!firstName.trim()) { setError("Please enter your first name"); return; }
      const pwError = validatePassword(password);
      if (pwError) { setError(pwError); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await supabase.signIn(email, password);
        if (data.access_token) {
          // Persist session if remember me checked
          if (rememberMe) localStorage.setItem("sf_session", JSON.stringify(data));
          onAuth(data);
        } else setError(data.error_description || "Login failed — check your email and password");
      } else {
        const data = await supabase.signUp(email, password);
        if (data.user) {
          if (rememberMe) localStorage.setItem("sf_session", JSON.stringify(data));
          logAudit(data.user.id, email, "user_signup", {}, data.access_token);
          // Save profile to Supabase
          try {
            const profRes = await fetch(SUPABASE_URL + "/rest/v1/profiles", {
              method: "POST",
              headers: { 
                apikey: SUPABASE_ANON_KEY, 
                Authorization: "Bearer " + data.access_token, 
                "Content-Type": "application/json", 
                Prefer: "resolution=merge-duplicates,return=minimal" 
              },
              body: JSON.stringify({ id: data.user.id, name: (firstName.trim() + " " + lastName.trim()).trim(), start_weight: parseFloat(weight) || null, goal_weight: parseFloat(goal) || null }),
            });
            if (!profRes.ok) {
              const errText = await profRes.text();
              console.error("Profile save HTTP error:", profRes.status, errText);
            }
          } catch(e) { console.error("Profile save failed", e); }
          const profile = { name: (firstName.trim() + " " + lastName.trim()).trim(), start_weight: parseFloat(weight) || null, goal_weight: parseFloat(goal) || null };
          onAuth({ ...data, isNew: true, profile });
        } else setError(data.error_description || "Signup failed");
      }
    } catch { setError("Connection error — please try again"); }
    setLoading(false);
  };

  // Password strength indicator for signup
  const pwStrength = mode === "signup" && password.length > 0 ? (
    password.length >= 8 && /\d/.test(password) && /[A-Z]/.test(password) ? "strong" :
    password.length >= 8 && /\d/.test(password) ? "good" : "weak"
  ) : null;

  const strengthColor = { strong: "#00cc88", good: TEAL, weak: "#ff6b6b" };
  const strengthLabel = { strong: "Strong", good: "Good", weak: "Too weak" };

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, #0a1518 0%, ${BG} 50%, #0d1e22 100%)`,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <img src={branding.logoUrl} alt="" style={{ height: 110, width: "auto", marginBottom: 16 }} />
        <div style={{ fontSize: 22, fontWeight: 900, color: TEXT, lineHeight: 1.1,
          fontFamily: "'Montserrat', sans-serif", marginBottom: 6, letterSpacing: "-0.01em" }}>
          {branding.appName.split(" ").slice(0,-1).join(" ")} <span style={{ color: TEAL }}>{branding.appName.split(" ").slice(-1)[0]}</span>
        </div>
        <div style={{ fontSize: 14, color: MUTED, fontStyle: "italic",
          fontFamily: "'Playfair Display', Georgia, serif" }}>{branding.tagline}</div>
      </div>

      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ display: "flex", background: CARD, borderRadius: 999, padding: 4, marginBottom: 24,
          border: `1px solid ${BORDER}`, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          {["login", "signup"].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(""); setPassword(""); }}
              style={{ flex: 1, background: mode === m ? CORAL : "transparent",
                border: "none", borderRadius: 999, padding: "10px", fontSize: 13,
                fontWeight: 700, color: mode === m ? "#fff" : MUTED, cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.05em",
                textTransform: "uppercase", transition: "all 0.25s",
                boxShadow: mode === m ? "0 2px 12px rgba(244,97,58,0.4)" : "none" }}>
              {m === "login" ? "Sign In" : "Get Started"}
            </button>
          ))}
        </div>

        {mode === "signup" && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
              <input placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
            </div>
          </>
        )}

        <form onSubmit={e => { e.preventDefault(); handle(); }} autoComplete="on">
        <input placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
          style={styles.input} type="email" autoComplete="email" />

        {/* Password field with eyeball */}
        <div style={{ position: "relative", marginBottom: 4 }}>
          <input
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            style={{ ...styles.input, marginBottom: 0, paddingRight: 48 }}
          />
          <button
            type="button" onClick={() => setShowPassword(!showPassword)}
            style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", color: MUTED,
              fontSize: 18, padding: 0, lineHeight: 1 }}>
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        {/* Password strength bar for signup */}
        {pwStrength && (
          <div style={{ marginBottom: 12, marginTop: 6 }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
              {["weak", "good", "strong"].map((level, i) => (
                <div key={level} style={{ flex: 1, height: 4, borderRadius: 999,
                  background: ["weak","good","strong"].indexOf(pwStrength) >= i
                    ? strengthColor[pwStrength] : CARD2,
                  transition: "background 0.25s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: strengthColor[pwStrength], textAlign: "right" }}>
              {strengthLabel[pwStrength]}
            </div>
          </div>
        )}

        {mode === "signup" && (
          <div style={{ background: CARD2, borderRadius: 12, padding: "12px 14px",
            marginBottom: 12, marginTop: 4, border: `1px solid ${BORDER}`,
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: TEAL, marginBottom: 8,
              letterSpacing: "0.15em", textTransform: "uppercase",
              fontFamily: "'Montserrat', sans-serif" }}>
              Password must include:
            </div>
            {[
              { label: "At least 8 characters", met: password.length >= 8 },
              { label: "At least one number (like 1, 2, 3...)", met: /\d/.test(password) },
              { label: "At least one capital letter (like A, B, C...)", met: /[A-Z]/.test(password) },
            ].map(({ label, met }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 8,
                marginBottom: 5, fontSize: 13 }}>
                <span style={{ color: met ? "#00cc88" : MUTED, fontSize: 15, lineHeight: 1 }}>
                  {met ? "✓" : "○"}
                </span>
                <span style={{ color: met ? "#00cc88" : MUTED, textDecoration: met ? "none" : "none",
                  transition: "color 0.2s" }}>
                  {label}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: MUTED, marginTop: 8, paddingTop: 8,
              borderTop: `1px solid ${BORDER}` }}>
              💡 Example of a strong password: <span style={{ color: TEXT }}>Fluids2024!</span>
            </div>
          </div>
        )}

        {mode === "signup" && (
          <>
            <input placeholder="Current weight (lbs)" value={weight}
              onChange={e => setWeight(e.target.value)} style={styles.input} type="number" step="0.1" />
            <input placeholder="Goal weight (lbs)" value={goal}
              onChange={e => setGoal(e.target.value)} style={styles.input} type="number" step="0.1" />
          </>
        )}

        {/* Remember me */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, marginTop: 4 }}>
          <div onClick={() => setRememberMe(!rememberMe)}
            style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${rememberMe ? TEAL : MUTED}`,
              background: rememberMe ? TEAL : "transparent", cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}>
            {rememberMe && <span style={{ color: "#000", fontSize: 13, fontWeight: 900 }}>✓</span>}
          </div>
          <span style={{ fontSize: 13, color: MUTED, cursor: "pointer" }} onClick={() => setRememberMe(!rememberMe)}>
            Keep me signed in
          </span>
        </div>

        {error && (
          <div style={{ color: "#ff6b6b", fontSize: 13, marginBottom: 12, background: "rgba(255,107,107,0.08)",
            borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(255,107,107,0.2)",
            fontFamily: "'Open Sans', sans-serif" }}>
            {error}
          </div>
        )}

        <button type="submit" disabled={loading} style={styles.btn()}>
          {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
        </button>
        </form>
      </div>
    </div>
  );
}

function DashboardScreen({ user, entries, session, onFollowUp, branding = { coachName: "Tina" } }) {
  const latest = entries[0];
  const weights = entries.filter(e => e.morning_weight).map(e => ({ w: e.morning_weight, d: e.created_at?.slice(5, 10) })).reverse().slice(-14);
  const delta = weights.length >= 2 ? (weights[weights.length - 1].w - weights[0].w).toFixed(1) : null;

  const [goalHistory, setGoalHistory] = useState([]);
  const [showGoalInput, setShowGoalInput] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [goalNote, setGoalNote] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  useEffect(() => {
    if (!session?.access_token || !session?.user?.id) return;
    fetch(`${SUPABASE_URL}/rest/v1/goal_history?user_id=eq.${session.user.id}&order=set_at.asc`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}` }
    }).then(r => r.json()).then(rows => {
      if (Array.isArray(rows)) setGoalHistory(rows);
    }).catch(() => {});
  }, [session]);

  const saveNewGoal = async () => {
    if (!newGoal || isNaN(parseFloat(newGoal))) return;
    setSavingGoal(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/goal_history`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify({ user_id: session.user.id, goal_weight: parseFloat(newGoal),
          note: goalNote || null, is_original: false }),
      });
      const [saved] = await res.json();
      if (saved) {
        setGoalHistory(prev => [...prev, saved]);
        setShowGoalInput(false);
        setNewGoal("");
        setGoalNote("");
        logAudit(session.user?.id, session.user?.email, "goal_updated", { goal_weight: parseFloat(newGoal), note: goalNote || null }, session.access_token);
      }
    } catch(e) { console.error(e); }
    setSavingGoal(false);
  };

  const currentGoal = goalHistory.length > 0 ? goalHistory[goalHistory.length - 1].goal_weight : user.goal_weight;

  return (
    <div style={styles.page}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "'Montserrat', sans-serif",
          letterSpacing: "-0.01em" }}>Good {new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 17 ? "Afternoon" : "Evening"}{user.name ? `, ${user.name}` : ""} 👋</div>
        <div style={{ fontSize: 13, color: MUTED, marginTop: 4, fontFamily: "'Open Sans', sans-serif" }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
      </div>

      <div style={{ ...styles.card, borderColor: `${AMBER}44`, textAlign: "center",
        background: `linear-gradient(145deg, ${CARD} 0%, #1e2c20 100%)` }}>
        <div style={{ fontSize: 13, color: MUTED }}>Active streak</div>
        <div style={{ fontSize: 40, fontWeight: 900, color: AMBER, fontFamily: "'Montserrat', sans-serif", textShadow: `0 0 20px ${AMBER}55` }}>{entries.length}</div>
        <div style={{ fontSize: 13, color: MUTED }}>days logged</div>
      </div>
      {/* Goal History Card */}
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={styles.cardTitle}>🎯 Goal History</div>
          {!showGoalInput && (
            <button onClick={() => setShowGoalInput(true)}
              style={{ background: "transparent", border: `1px solid ${TEAL}`, color: TEAL,
                borderRadius: 999, padding: "5px 14px", fontSize: 11, cursor: "pointer",
                fontWeight: 700, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.05em" }}>
              + Adjust Goal
            </button>
          )}
        </div>

        {goalHistory.length === 0 && !showGoalInput && (
          user.goal_weight ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: TEAL, marginTop: 4,
                boxShadow: `0 0 8px ${TEAL}`, flexShrink: 0 }} />
              <div>
                <span style={{ fontSize: 18, fontWeight: 900, color: TEAL,
                  fontFamily: "'Montserrat', sans-serif" }}>{user.goal_weight} lbs</span>
                <span style={{ fontSize: 10, color: TEAL, fontWeight: 700,
                  background: `${TEAL}22`, borderRadius: 999, padding: "2px 8px", marginLeft: 8,
                  fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.08em" }}>CURRENT GOAL</span>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Set at signup</div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: MUTED, textAlign: "center", padding: "12px 0" }}>
              No goal set yet. Hit "Adjust Goal" to set your target weight.
            </div>
          )
        )}

        {goalHistory.map((g, i) => (
          <div key={g.id} style={{ display: "flex", alignItems: "flex-start", gap: 12,
            marginBottom: 12, opacity: i < goalHistory.length - 1 ? 0.6 : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%",
                background: i === 0 ? AMBER : TEAL,
                boxShadow: i === goalHistory.length - 1 ? `0 0 8px ${TEAL}` : "none" }} />
              {i < goalHistory.length - 1 && (
                <div style={{ width: 2, height: 28, background: BORDER, margin: "3px 0" }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: i === goalHistory.length - 1 ? TEAL : MUTED,
                  fontFamily: "'Montserrat', sans-serif" }}>{g.goal_weight} lbs</span>
                {i === 0 && <span style={{ fontSize: 10, color: AMBER, fontWeight: 700,
                  background: `${AMBER}22`, borderRadius: 999, padding: "2px 8px",
                  fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.08em" }}>ORIGINAL</span>}
                {i === goalHistory.length - 1 && i > 0 && <span style={{ fontSize: 10, color: TEAL, fontWeight: 700,
                  background: `${TEAL}22`, borderRadius: 999, padding: "2px 8px",
                  fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.08em" }}>CURRENT</span>}
              </div>
              <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                {i === 0 ? "Goal set" : "Updated"} {new Date(g.set_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {new Date(g.set_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
              {g.note && <div style={{ fontSize: 12, color: TEXT, marginTop: 3, fontStyle: "italic" }}>"{g.note}"</div>}
            </div>
          </div>
        ))}

        {showGoalInput && (
          <div style={{ background: CARD2, borderRadius: 12, padding: 16, marginTop: 8,
            border: `1px solid ${TEAL}44` }}>
            <div style={{ fontSize: 12, color: TEAL, fontWeight: 700, marginBottom: 10,
              fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em" }}>
              {goalHistory.length === 0 ? "SET YOUR GOAL" : "ADJUST GOAL"}
            </div>
            <input type="number" placeholder="New goal weight (lbs)" value={newGoal}
              onChange={e => setNewGoal(e.target.value)} step="0.1"
              style={{ ...styles.input, marginBottom: 8 }} />
            <input type="text" placeholder="Reason (optional — e.g. revised timeline)"
              value={goalNote} onChange={e => setGoalNote(e.target.value)}
              style={{ ...styles.input, marginBottom: 12 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveNewGoal} disabled={savingGoal}
                style={{ ...styles.btn(), flex: 1, padding: "10px", fontSize: 13, marginTop: 0 }}>
                {savingGoal ? "Saving..." : goalHistory.length === 0 ? "Set Goal" : "Lock In & Update"}
              </button>
              <button onClick={() => { setShowGoalInput(false); setNewGoal(""); setGoalNote(""); }}
                style={{ ...styles.btn("secondary"), flex: 1, padding: "10px", fontSize: 13, marginTop: 0 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>


      <div style={styles.card}>
        <div style={styles.cardTitle}>Current Weight</div>
        {(latest?.morning_weight || user.start_weight) ? (
          <WeightDial
            current={latest?.morning_weight || user.start_weight}
            goal={currentGoal}
            start={user.start_weight || (latest?.morning_weight)}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "32px 0", color: MUTED }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚖️</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Complete your first check-in</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Your weight progress will appear here</div>
          </div>
        )}
        {delta && (
          <div style={{ textAlign: "center", marginTop: 8, fontSize: 15, fontWeight: 700,
            color: parseFloat(delta) <= 0 ? "#00cc88" : "#ff6b6b" }}>
            {parseFloat(delta) <= 0 ? "▼" : "▲"} {Math.abs(delta)} lbs since you started
          </div>
        )}
      </div>

      {weights.length > 1 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>14-Day Trend</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 60 }}>
            {weights.map((w, i) => {
              const max = Math.max(...weights.map(x => x.w));
              const min = Math.min(...weights.map(x => x.w));
              const range = max - min || 1;
              const h = 10 + ((w.w - min) / range) * 50;
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: "100%", height: h,
                    background: i === weights.length - 1
                      ? `linear-gradient(180deg, ${TEAL_LIGHT} 0%, ${TEAL} 100%)`
                      : TEAL,
                    borderRadius: "4px 4px 0 0",
                    opacity: i === weights.length - 1 ? 1 : 0.35,
                    boxShadow: i === weights.length - 1 ? `0 0 8px ${TEAL}88` : "none" }} />
                  <div style={{ fontSize: 8, color: MUTED }}>{w.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {latest?.ai_response && (() => {
        // Pull a one-line summary from the AI response — first non-empty, non-header line
        const lines = latest.ai_response.split("\n").map(l => l.trim())
          .filter(l => l && !l.startsWith("#") && !l.startsWith("|") && !l.startsWith("---"));
        const preview = lines[0]?.replace(/\*\*/g, "").slice(0, 90) || "Analysis complete";
        const entryDate = latest.entry_date || latest.created_at?.slice(0, 10);
        const dateLabel = entryDate ? new Date(entryDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "Today";
        return (
          <div onClick={() => window._sfOpenEntry ? window._sfOpenEntry(0) : window._sfSetTab && window._sfSetTab("history")}
            style={{ ...styles.card, borderColor: `${TEAL}33`, cursor: "pointer",
              background: `linear-gradient(145deg, ${CARD} 0%, #162830 100%)`,
              transition: "border-color 0.2s, box-shadow 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
            onMouseLeave={e => e.currentTarget.style.borderColor = `${TEAL}33`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
                  <div style={styles.cardTitle}>Latest Coach Feedback</div>
                  {!latest.followup_response && latest.ai_response?.includes("QUESTIONS:") && (
                    <span style={{ fontSize: 15, display: "inline-block", animation: "brainGlow 2.4s ease-in-out infinite" }}>🧠</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
                  ✅ Completed — {dateLabel}
                </div>
                {/* Quick stat pills */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                  {latest.energy_level && <span style={{ fontSize: 11, background: `${TEAL}22`, color: TEAL,
                    borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>⚡ Energy {latest.energy_level}/10</span>}
                  {latest.mood_level && <span style={{ fontSize: 11, background: `${CORAL}22`, color: CORAL_WARM,
                    borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>😊 Mood {latest.mood_level}/10</span>}
                  {latest.stress_level && <span style={{ fontSize: 11, background: `rgba(180,180,100,0.15)`, color: AMBER,
                    borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>🎯 Stress {latest.stress_level}/10</span>}
                  {latest.morning_weight && <span style={{ fontSize: 11, background: `rgba(100,180,100,0.15)`, color: "#7ec87e",
                    borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>⚖️ {latest.morning_weight} lbs</span>}
                </div>
                <div style={{ fontSize: 12, color: MUTED, fontStyle: "italic", lineHeight: 1.5 }}>
                  "{preview}..."
                </div>
              </div>
              <div style={{ color: TEAL, fontSize: 18, marginLeft: 12, marginTop: 2 }}>→</div>
            </div>
          </div>
        );
      })()}
      {latest && !latest.followup_response && latest.ai_response?.includes("QUESTIONS:") && (
        <EntryFollowUp entry={latest} onFollowUp={onFollowUp} />
      )}
    </div>
  );
}

function CheckInScreen({ user, onSubmit, onFollowUp, todayEntry, previousEntry, branding = { coachName: "Tina" } }) {
  const [step, setStep] = useState(0);
  const [weight, setWeight] = useState("");
  const [sleep, setSleep] = useState(7);
  const [energy, setEnergy] = useState(5);
  const [mood, setMood] = useState(5);
  const [stress, setStress] = useState(5);
  const [workout, setWorkout] = useState(false);
  const [meds, setMeds] = useState([]);
  const [supps, setSupps] = useState([]);
  const [foods, setFoods] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [followUpAnswers, setFollowUpAnswers] = useState({});
  const [followUpResponse, setFollowUpResponse] = useState(null);
  const [followUpLoading, setFollowUpLoading] = useState(false);

  // Rehydrate follow-up questions from a saved entry so the answer boxes
  // appear when a user revisits today's check-in (not just right after submit).
  useEffect(() => {
    if (!todayEntry || todayEntry.followup_response || followUpQuestions.length > 0) return;
    if (!todayEntry.ai_response) return;
    const qSplit = todayEntry.ai_response.split(/\*?\*?QUESTIONS:\*?\*?/i);
    if (qSplit.length > 1) {
      const qLines = qSplit[1]
        .split("\n")
        .filter(l => /^\d+\./.test(l.trim()))
        .map(l => l.replace(/^\d+\.\s*/, "").trim())
        .filter(Boolean);
      if (qLines.length > 0) setFollowUpQuestions(qLines);
    }
  }, [todayEntry]);
  const [showAddMed, setShowAddMed] = useState(false);
  const [showAddSupp, setShowAddSupp] = useState(false);
  const [showAddFood, setShowAddFood] = useState(false);


  const submit = async () => {
    setLoading(true);
    setStep(4); // Show spinner immediately while AI processes
    try {
      // Load AI settings from localStorage (set in admin AI Settings panel)
      let aiProvider = localStorage.getItem("sf_ai_provider") || "anthropic";
      let aiApiKey = localStorage.getItem("sf_ai_key") || "";
      let aiModel = localStorage.getItem("sf_ai_model") || "claude-sonnet-4-20250514";

      const prevEntryDate = previousEntry?.entry_date || previousEntry?.created_at?.slice(0, 10);
      const daysSincePrev = prevEntryDate ? Math.round((new Date() - new Date(prevEntryDate)) / 86400000) : null;
      const prevContext = previousEntry ? `
PREVIOUS CHECK-IN (${prevEntryDate}${daysSincePrev !== null ? ` — ${daysSincePrev} day${daysSincePrev === 1 ? "" : "s"} ago` : ""}):
Weight: ${previousEntry.morning_weight || "not logged"}lbs
Sleep: ${previousEntry.sleep_hours || "?"}hrs | Energy: ${previousEntry.energy_level || "?"}/10 | Mood: ${previousEntry.mood_level || "?"}/10 | Stress: ${previousEntry.stress_level || "?"}/10
Workout: ${previousEntry.workout_done ? "Yes" : "No"}
${previousEntry.followup_qa ? "Previous Q&A:\n" + previousEntry.followup_qa : ""}
Coach's focus from that session: ${previousEntry.ai_response ? previousEntry.ai_response.split("ONE KEY FOCUS FOR TOMORROW:")[1]?.split("\n")[1]?.trim() || "" : ""}
` : "";

      const summary = `
USER PROFILE: Start weight: ${user.start_weight}lbs | Goal: ${user.goal_weight}lbs

TODAY'S CHECK-IN:
Morning weight: ${weight}lbs
Sleep: ${sleep} hours
Energy: ${energy}/10 (${ENERGY_LABELS[energy]})
Mood: ${mood}/10 (${MOOD_LABELS[mood]})
Stress: ${stress}/10 (${STRESS_LABELS[stress]})
Workout completed: ${workout ? "Yes" : "No"}

MEDICATIONS TODAY:
${meds.length ? meds.map(m => `- ${m.name}${m.amount ? " (" + m.amount + ")" : ""}${m.time ? " at " + m.time : ""}`).join("\n") : "None logged"}

SUPPLEMENTS TODAY:
${supps.length ? supps.map(s => `- ${s.name}${s.amount ? " (" + s.amount + ")" : ""}${s.time ? " at " + s.time : ""}`).join("\n") : "None logged"}

FOOD & DRINK TODAY:
${foods.length ? foods.map(f => `- ${f.name}${f.time ? " at " + f.time : ""}${f.notes ? " (" + f.notes + ")" : ""}`).join("\n") : "Nothing logged yet"}

ADDITIONAL NOTES: ${notes || "None"}
      `.trim();

      const messages = [{ role: "user", content: prevContext + summary }];

      // Direct Anthropic API call
      let aiText = "Unable to generate analysis.";
      if (!aiApiKey) {
        aiText = "No API key set. Log in as admin, go to AI Settings, and save your Anthropic key.";
      } else {
        const aiRes = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": aiApiKey,
            "anthropic-version": "2023-06-01",
            "anthropic-dangerous-direct-browser-access": "true",
          },
          body: JSON.stringify({
            model: aiModel,
            max_tokens: 1024,
            system: SYSTEM_PROMPT,
            messages,
          }),
        });
        const aiData = await aiRes.json();
        aiText = aiData.content?.[0]?.text || aiData.error?.message || "Unable to generate analysis.";
      }
      // Parse follow-up questions from AI response
      // Parse questions — handle both **QUESTIONS:** and plain QUESTIONS:
      const qSplit = aiText.split(/\*?\*?QUESTIONS:\*?\*?/i);
      if (qSplit.length > 1) {
        const qLines = qSplit[1].split("\n").filter(l => /^\d+\./.test(l.trim())).map(l => l.replace(/^\d+\.\s*/, '').trim()).filter(Boolean);
        if (qLines.length > 0) setFollowUpQuestions(qLines);
      }
      setAiResponse(aiText);
      if (onSubmit) onSubmit({ weight: parseFloat(weight), sleep, energy, mood, stress, workout, meds, supps, foods, notes, ai_response: aiText });
    } catch (e) {
      setAiResponse("Connection error. Please try again.");
    }
    setLoading(false);
  };

  const submitFollowUp = async () => {
    if (Object.keys(followUpAnswers).length === 0) return;
    setFollowUpLoading(true);
    try {
      let aiProvider = localStorage.getItem("sf_ai_provider") || "anthropic";
      let aiApiKey = localStorage.getItem("sf_ai_key") || "";
      let aiModel = localStorage.getItem("sf_ai_model") || "claude-sonnet-4-20250514";
      const qaText = followUpQuestions.map((q, i) => `Q: ${q}\nA: ${followUpAnswers[i] || "(no answer)"}`).join("\n\n");
      const followUpPrompt = `The client just answered your follow-up questions:\n\n${qaText}\n\nGive a brief, warm closing response (2-4 sentences max). Acknowledge their answers, give one sharp insight or encouragement based on what they said, and close strong. No lists, no headers — just direct coach-to-client.`;
      let closeText = "";
      if (aiApiKey) {
        const aiRes = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": aiApiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
          body: JSON.stringify({ model: aiModel, max_tokens: 300, system: SYSTEM_PROMPT, messages: [{ role: "user", content: followUpPrompt }] }),
        });
        const aiData = await aiRes.json();
        closeText = aiData.content?.[0]?.text || "";
      }
      setFollowUpResponse(closeText);
      // Save Q&A + closing response back to the entry
      if (onFollowUp) onFollowUp({ qa: qaText, closing: closeText });
    } catch(e) { setFollowUpResponse("Got it — thanks for sharing that. Keep going strong."); }
    setFollowUpLoading(false);
  };

  // If they already have a today entry BUT just submitted (step 4), stay on analysis
  // Only show "already checked in" if they navigate back later
  if (todayEntry && step < 4) return (
    <div style={styles.page}>
      <div style={{ ...styles.card, padding: "20px 20px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 22 }}>✅</div>
          <div style={{ fontSize: 13, color: MUTED }}>Come back tomorrow to log your next day.</div>
        </div>
        <EntryHeader e={todayEntry} />
      </div>
      {todayEntry.ai_response && <AIResponse response={todayEntry.ai_response} />}
      {todayEntry.followup_qa && (
        <div style={{ ...styles.card, border: `1px solid ${TEAL}33` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 10, textTransform: "uppercase" }}>💬 Your Responses to Your Coach</div>
          <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6, whiteSpace: "pre-line" }}>{todayEntry.followup_qa}</div>
        </div>
      )}
      {todayEntry.followup_response && (
        <div style={{ ...styles.card, border: `1px solid ${TEAL}55`, background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.08) 100%)` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>💚 Coach's Closing Note</div>
          <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6 }}>{todayEntry.followup_response}</div>
        </div>
      )}
      {!todayEntry.followup_response && followUpQuestions.length > 0 && !followUpResponse && (
        <div style={{ ...styles.card, marginTop: 8, border: `2px solid ${CORAL}88`, boxShadow: `0 0 16px rgba(244,97,58,0.15)` }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: CORAL, marginBottom: 4 }}>⚠️ Action Required — Your coach needs your answers</div>
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>Answer the questions below. When you finish your last answer and tap away, your coach will respond automatically.</div>
          {followUpQuestions.map((q, i) => {
            const isLast = i === followUpQuestions.length - 1;
            return (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: TEXT, fontWeight: 600, marginBottom: 6 }}>{i + 1}. {q}</div>
                <textarea
                  placeholder="Tap to answer..."
                  value={followUpAnswers[i] || ""}
                  onChange={e => setFollowUpAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                  onBlur={() => {
                    if (isLast && (followUpAnswers[i] || "").trim()) submitFollowUp();
                  }}
                  style={{ ...styles.input, minHeight: 80, resize: "none", marginBottom: 4, fontSize: 13 }}
                />
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 8, fontStyle: "italic" }}>
                  {isLast ? "Tap outside this field to save your answer and send to your coach →" : "Tap outside this field to save your answer"}
                </div>
              </div>
            );
          })}
          {followUpLoading && (
            <div style={{ textAlign: "center", padding: "12px 0", color: TEAL, fontSize: 13, fontWeight: 600 }}>
              <span style={{ marginRight: 8 }}>🧠</span>Your coach is reading your responses...
            </div>
          )}
        </div>
      )}
      {followUpResponse && (
        <div style={{ ...styles.card, marginTop: 8, border: `1px solid ${TEAL}66`, background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.06) 100%)` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 10, textTransform: "uppercase" }}>💚 Your Coach</div>
          <div style={{ fontSize: 14, color: TEXT, lineHeight: 1.6 }}>{followUpResponse}</div>
        </div>
      )}
    </div>
  );

  const steps = ["Morning", "Meds & Supps", "Food & Drink", "Wrap Up", "Analysis"];

  return (
    <div style={styles.page}>
      {/* Progress */}
      <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 999,
            background: i < step ? CORAL : i === step ? TEAL : CARD2,
            boxShadow: i === step ? `0 0 8px ${TEAL}66` : i < step ? `0 0 6px ${CORAL}44` : "none",
            transition: "all 0.3s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
        ))}
      </div>
      <div style={{ fontSize: 10, color: TEAL, letterSpacing: "0.2em", textTransform: "uppercase",
        marginBottom: 16, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>
        Step {step + 1} — {steps[step]}
      </div>

      {/* Step 0: Morning */}
      {step === 0 && (
        <div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Morning Weight</div>
            <input placeholder="e.g. 187.4" value={weight} onChange={e => setWeight(e.target.value)}
              type="number" step="0.1" style={{ ...styles.input, fontSize: 28, fontWeight: 900,
                textAlign: "center", color: TEAL, marginBottom: 0,
                fontFamily: "'Montserrat', sans-serif", letterSpacing: "-0.02em" }} />
            <div style={{ textAlign: "center", fontSize: 12, color: MUTED, marginTop: 6 }}>POUNDS</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Sleep</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: MUTED }}>Hours slept</span>
              <span style={{ fontSize: 22, fontWeight: 900, color: TEAL,
                fontFamily: "'Montserrat', sans-serif" }}>{sleep}h</span>
            </div>
            <input type="range" min="2" max="12" step="0.5" value={sleep}
              onChange={e => setSleep(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: TEAL }} />
            <div style={{ textAlign: "center", marginTop: 8, fontSize: 18, fontWeight: 800, color: TEAL,
              fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}>
              {sleep < 5 ? "Critically Low" : sleep < 6.5 ? "Under-Rested" : sleep < 7.5 ? "Adequate" : sleep < 9 ? "Well Rested" : "Fully Recovered"}
            </div>
          </div>
          <div style={styles.card}>
            <ScaleSlider label="Energy Level" value={energy} onChange={setEnergy} labelMap={ENERGY_LABELS} />
            <ScaleSlider label="Mood" value={mood} onChange={setMood} labelMap={MOOD_LABELS} color="#cc88ff" />
          </div>
          <button onClick={() => {
            const w = parseFloat(weight);
            if (weight && (w < 50 || w > 700)) {
              alert(`${weight} lbs doesn't look right — please check your entry.`);
              return;
            }
            const prev = previousEntry?.morning_weight;
            if (weight && prev && Math.abs(w - prev) > 20) {
              if (!window.confirm(`Your last recorded weight was ${prev} lbs. You entered ${w} lbs — that's a ${Math.abs(w - prev).toFixed(1)} lb difference. Does that look right?`)) return;
            }
            setStep(1);
          }} style={styles.btn()}>Next →</button>
        </div>
      )}

      {/* Step 1: Meds & Supplements */}
      {step === 1 && (
        <div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Medications</div>
            {meds.map((m, i) => <LogRow key={m.id} item={m} fields={["name","amount","time"]} onRemove={() => setMeds(meds.filter((_, j) => j !== i))} onEdit={updated => setMeds(meds.map((x, j) => j === i ? updated : x))} />)}
            {showAddMed
              ? <AddItemForm onAdd={m => { setMeds([...meds, m]); setShowAddMed(false); }} placeholder="Medication name" fields={["name", "amount", "time"]} />
              : <button onClick={() => setShowAddMed(true)} style={{ ...styles.btn("secondary"), marginTop: 0 }}>+ Add Medication</button>
            }
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Supplements</div>
            {supps.map((s, i) => <LogRow key={s.id} item={s} fields={["name","amount","time"]} onRemove={() => setSupps(supps.filter((_, j) => j !== i))} onEdit={updated => setSupps(supps.map((x, j) => j === i ? updated : x))} />)}
            {showAddSupp
              ? <AddItemForm onAdd={s => { setSupps([...supps, s]); setShowAddSupp(false); }} placeholder="Supplement name" fields={["name", "amount", "time"]} />
              : <button onClick={() => setShowAddSupp(true)} style={{ ...styles.btn("secondary"), marginTop: 0 }}>+ Add Supplement</button>
            }
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(0)} style={{ ...styles.btn("secondary"), flex: 1 }}>← Back</button>
            <button onClick={() => setStep(2)} style={{ ...styles.btn(), flex: 2 }}>Next →</button>
          </div>
        </div>
      )}

      {/* Step 2: Food & Drink */}
      {step === 2 && (
        <div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Food & Drink Log</div>
            {foods.map((f, i) => <LogRow key={f.id} item={f} fields={["name","time","notes"]} onRemove={() => setFoods(foods.filter((_, j) => j !== i))} onEdit={updated => setFoods(foods.map((x, j) => j === i ? updated : x))} />)}
            {showAddFood
              ? <AddItemForm onAdd={f => { setFoods([...foods, f]); setShowAddFood(false); }} placeholder="What did you eat or drink?" fields={["name", "time", "notes"]} />
              : <button onClick={() => setShowAddFood(true)} style={{ ...styles.btn("secondary"), marginTop: 0 }}>+ Add Food / Drink</button>
            }
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(1)} style={{ ...styles.btn("secondary"), flex: 1 }}>← Back</button>
            <button onClick={() => setStep(3)} style={{ ...styles.btn(), flex: 2 }}>Next →</button>
          </div>
        </div>
      )}

      {/* Step 3: Wrap Up */}
      {step === 3 && (
        <div>
          <div style={styles.card}>
            <ScaleSlider label="Stress Level" value={stress} onChange={setStress} labelMap={STRESS_LABELS} color="#ff9944" />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
              <div style={{ fontSize: 14, color: MUTED, flex: 1 }}>Workout completed today?</div>
              <button onClick={() => setWorkout(!workout)}
                style={{ background: workout ? CORAL : CARD2, border: `2px solid ${workout ? CORAL : BORDER}`,
                  borderRadius: 999, padding: "8px 20px", color: workout ? "#fff" : MUTED,
                  fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {workout ? "✓ Yes" : "No"}
              </button>
            </div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Anything else your coach should know?</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="How are you feeling? Any struggles, wins, or context..."
              style={{ ...styles.input, minHeight: 100, resize: "none", marginBottom: 0 }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(2)} style={{ ...styles.btn("secondary"), flex: 1 }}>← Back</button>
            <button onClick={submit} disabled={loading} style={{ ...styles.btn(), flex: 2 }}>
              {loading ? "Analyzing..." : "Get My Analysis →"}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: AI Response */}
      {step === 4 && (
        <div>
          <AIResponse response={aiResponse} loading={loading} loadingMessage="Your coach is reviewing your day..." />
          {!loading && followUpQuestions.length > 0 && !followUpResponse && (
            <div style={{ ...styles.card, marginTop: 8, border: `2px solid ${CORAL}88`, boxShadow: `0 0 16px rgba(244,97,58,0.15)` }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: CORAL, marginBottom: 4 }}>⚠️ Action Required — Your coach needs your answers</div>
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>Answer the questions below. When you finish your last answer and tap away, your coach will respond automatically.</div>
              {followUpQuestions.map((q, i) => {
                const isLast = i === followUpQuestions.length - 1;
                return (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 13, color: TEXT, fontWeight: 600, marginBottom: 6 }}>{i + 1}. {q}</div>
                    <textarea
                      placeholder="Tap to answer..."
                      value={followUpAnswers[i] || ""}
                      onChange={e => setFollowUpAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                      onBlur={() => {
                        if (isLast && (followUpAnswers[i] || "").trim()) {
                          submitFollowUp();
                        }
                      }}
                      style={{ ...styles.input, minHeight: 80, resize: "none", marginBottom: 4, fontSize: 13 }}
                    />
                    <div style={{ fontSize: 11, color: MUTED, marginBottom: 8, fontStyle: "italic" }}>
                      {isLast ? "Tap outside this field to save your answer and send to your coach →" : "Tap outside this field to save your answer"}
                    </div>
                  </div>
                );
              })}
              {followUpLoading && (
                <div style={{ textAlign: "center", padding: "12px 0", color: TEAL, fontSize: 13, fontWeight: 600 }}>
                  <span style={{ marginRight: 8 }}>🧠</span>Your coach is reading your responses...
                </div>
              )}
            </div>
          )}
          {followUpResponse && (
            <>
              <div style={{ ...styles.card, marginTop: 8, border: `1px solid ${TEAL}66`, background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.06) 100%)` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 10, textTransform: "uppercase" }}>💚 Your Coach</div>
                <div style={{ fontSize: 14, color: TEXT, lineHeight: 1.6 }}>{followUpResponse}</div>
              </div>
              <div style={{ ...styles.card, marginTop: 8, textAlign: "center", padding: "20px 24px",
                background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.04) 100%)`,
                border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>🎉</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 6 }}>
                  Great job checking in today!
                </div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  You showed up, you logged it, and you engaged. That's the habit that gets results. Come back tomorrow and keep the streak going. 💪
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <div style={{ textAlign: "center", fontSize: 11, color: MUTED, padding: "8px 20px 24px", lineHeight: 1.6 }}>
        Your coach provides wellness coaching only — not medical advice. Always consult a qualified healthcare provider for medical decisions.
      </div>
    </div>
  );
}


// ── Shared entry header — used in admin, history, and checkin views ──
function EntryHeader({ e }) {
  const dateStr = (e.entry_date
    ? new Date(e.entry_date + "T12:00:00")
    : new Date(e.created_at)
  ).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "America/New_York" });

  const timeStr = new Date(e.created_at).toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/New_York"
  });

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{dateStr} <span style={{ fontSize: 11, color: MUTED, fontWeight: 400 }}>{timeStr} ET</span></div>
        {e.morning_weight && <div style={{ fontSize: 14, fontWeight: 700, color: TEAL }}>{e.morning_weight} lbs</div>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {e.energy_level && <span style={{ fontSize: 11, background: "rgba(255,200,0,0.12)", color: AMBER, borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>⚡ Energy {e.energy_level}/10</span>}
        {e.mood_level && <span style={{ fontSize: 11, background: "rgba(100,200,100,0.12)", color: "#7ec87e", borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>😊 Mood {e.mood_level}/10</span>}
        {e.stress_level && <span style={{ fontSize: 11, background: "rgba(244,97,58,0.12)", color: CORAL, borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>🎯 Stress {e.stress_level}/10</span>}
        {e.sleep_hours && <span style={{ fontSize: 11, background: "rgba(150,100,255,0.12)", color: "#b088ff", borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>😴 {e.sleep_hours} hrs</span>}
        {e.workout_done && <span style={{ fontSize: 11, background: "rgba(0,200,100,0.12)", color: "#00cc88", borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>✅ Workout</span>}
      </div>
    </div>
  );
}

// ── Interactive follow-up Q&A for any saved entry (history view) ──
function EntryFollowUp({ entry, onFollowUp }) {
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  // Parse questions from the saved ai_response (same regex as submission path)
  const qSplit = entry.ai_response ? entry.ai_response.split(/\*?\*?QUESTIONS:\*?\*?/i) : [];
  const questions = qSplit.length > 1
    ? qSplit[1].split("\n").filter(l => /^\d+\./.test(l.trim())).map(l => l.replace(/^\d+\.\s*/, "").trim()).filter(Boolean)
    : [];

  if (questions.length === 0 || entry.followup_response) return null;

  const submit = async () => {
    if (Object.keys(answers).length === 0) return;
    setLoading(true);
    try {
      const aiApiKey = localStorage.getItem("sf_ai_key") || "";
      const aiModel = localStorage.getItem("sf_ai_model") || "claude-sonnet-4-20250514";
      const qaText = questions.map((q, i) => `Q: ${q}\nA: ${answers[i] || "(no answer)"}`).join("\n\n");
      const prompt = `The client just answered your follow-up questions:\n\n${qaText}\n\nGive a brief, warm closing response (2-4 sentences max). Acknowledge their answers, give one sharp insight or encouragement based on what they said, and close strong. No lists, no headers — just direct coach-to-client.`;
      let closeText = "";
      if (aiApiKey) {
        const res = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": aiApiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
          body: JSON.stringify({ model: aiModel, max_tokens: 300, system: SYSTEM_PROMPT, messages: [{ role: "user", content: prompt }] }),
        });
        const data = await res.json();
        closeText = data.content?.[0]?.text || "";
      }
      setResponse(closeText);
      if (onFollowUp) onFollowUp(entry.id, { qa: qaText, closing: closeText });
    } catch(e) { setResponse("Got it — thanks for sharing that. Keep going strong."); }
    setLoading(false);
  };

  if (response) return (
    <div style={{ marginTop: 12, padding: "14px 16px", borderRadius: 12, background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.08) 100%)`, border: `1px solid ${TEAL}44` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>💚 Coach's Closing Note</div>
      <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6 }}>{response}</div>
    </div>
  );

  return (
    <div style={{ marginTop: 12, padding: "16px", borderRadius: 12, border: `2px solid ${CORAL}88`, boxShadow: `0 0 16px rgba(244,97,58,0.12)`, background: `rgba(244,97,58,0.04)` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 18, display: "inline-block", animation: "brainGlow 2.4s ease-in-out infinite" }}>🧠</span>
        <div style={{ fontSize: 14, fontWeight: 800, color: CORAL }}>Your coach needs your answers</div>
      </div>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>Answer the questions below. When you finish your last answer and tap away, your coach will respond automatically.</div>
      {questions.map((q, i) => {
        const isLast = i === questions.length - 1;
        return (
          <div key={i} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: TEXT, fontWeight: 600, marginBottom: 6 }}>{i + 1}. {q}</div>
            <textarea
              placeholder="Tap to answer..."
              value={answers[i] || ""}
              onChange={e => setAnswers(prev => ({ ...prev, [i]: e.target.value }))}
              onBlur={() => { if (isLast && (answers[i] || "").trim()) submit(); }}
              style={{ ...styles.input, minHeight: 80, resize: "none", marginBottom: 4, fontSize: 13 }}
            />
            <div style={{ fontSize: 11, color: MUTED, fontStyle: "italic" }}>
              {isLast ? "Tap outside this field to send to your coach →" : "Tap outside this field to save"}
            </div>
          </div>
        );
      })}
      {loading && (
        <div style={{ textAlign: "center", padding: "12px 0", color: TEAL, fontSize: 13, fontWeight: 600 }}>
          <span style={{ marginRight: 8 }}>🧠</span>Your coach is reading your responses...
        </div>
      )}
    </div>
  );
}

function HistoryScreen({ entries, initialExpanded = null, onFollowUp }) {
  const [expanded, setExpanded] = useState(initialExpanded);
  return (
    <div style={styles.page}>
      <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 20, fontFamily: "'Montserrat', sans-serif",
        letterSpacing: "-0.01em" }}>Your Journey</div>
      {entries.length === 0 && (
        <div style={{ ...styles.card, textAlign: "center", color: MUTED }}>No entries yet. Complete your first check-in!</div>
      )}
      {entries.map((e, i) => (
        <div key={i} style={{ ...styles.card, cursor: "pointer",
          transition: "border-color 0.25s, box-shadow 0.25s" }} onClick={() => setExpanded(expanded === i ? null : i)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <EntryHeader e={e} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {!e.followup_response && e.ai_response?.includes("QUESTIONS:") && (
                <span style={{ fontSize: 15, display: "inline-block", animation: "brainGlow 2.4s ease-in-out infinite" }}>🧠</span>
              )}
              <div style={{ color: TEAL, fontSize: 18, fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif" }}>{expanded === i ? "↑" : "↓"}</div>
            </div>
          </div>
          {expanded === i && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDER}` }} onClick={e => e.stopPropagation()}>

              {e.ai_response
                ? <AIResponse response={e.ai_response} />
                : <div style={{ color: MUTED, fontSize: 13 }}>No coach analysis for this entry.</div>
              }
              {e.followup_qa && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 10, textTransform: "uppercase" }}>💬 Your Responses to Your Coach</div>
                  <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6, whiteSpace: "pre-line" }}>{e.followup_qa}</div>
                </div>
              )}
              {e.followup_response && (
                <div style={{ marginTop: 12, padding: "14px 16px", borderRadius: 12, background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.08) 100%)`, border: `1px solid ${TEAL}44` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>💚 Coach's Closing Note</div>
                  <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6 }}>{e.followup_response}</div>
                </div>
              )}
              <EntryFollowUp entry={e} onFollowUp={onFollowUp} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ============================================================
// ADMIN PANEL
// ============================================================

// Audit logger — call this throughout the app to track actions
const logAudit = async (userId, email, action, metadata = {}, accessToken = null) => {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/audit_log`, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken || SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
        Prefer: "return=minimal",
      },
      body: JSON.stringify({ user_id: userId, email, action, metadata }),
    });
    if (!res.ok) {
      const body = await res.text();
      console.error(`[audit] INSERT rejected (${res.status}):`, body, { action, userId });
    }
  } catch(e) { console.error("[audit] Network error:", e, { action, userId }); }
};

function AdminDashboard({ session, onSignOut, branding = { appName: "Life Alignment Coach", tagline: "Dripped. Not Sipped.", coachName: "Tina", logoUrl: LOGO_URL }, onBrandingChange }) {
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [authUsers, setAuthUsers] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [mainTab, setMainTab] = useState("users"); // top-level: users | activity
  const [activeTab, setActiveTab] = useState("entries"); // user detail: entries | activity
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [nameMap, setNameMap] = useState({});
  const [showAddClient, setShowAddClient] = useState(false);
  const [viewMode, setViewMode] = useState(window.innerWidth >= 768 ? "table" : "cards");
  const [addClientForm, setAddClientForm] = useState({ name: "", email: "", password: "" });
  const [addClientLoading, setAddClientLoading] = useState(false);
  const [addClientError, setAddClientError] = useState("");
  const [addClientSuccess, setAddClientSuccess] = useState("");

  const headers = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };

  const handleAddClient = async () => {
    const { name, email, password } = addClientForm;
    if (!name.trim() || !email.trim() || !password.trim()) {
      setAddClientError("Name, email, and password are all required."); return;
    }
    setAddClientLoading(true); setAddClientError(""); setAddClientSuccess("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/invite-client`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password: password.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setAddClientError(data.error || "Failed to create client."); }
      else {
        setAddClientSuccess(`${name.trim()} added successfully.`);
        setAddClientForm({ name: "", email: "", password: "" });
        setShowAddClient(false);
        load();
      }
    } catch(e) { setAddClientError("Network error — please try again."); }
    setAddClientLoading(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [profRes, entRes, auditRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/profiles?select=*`, { headers }),
        fetch(`${SUPABASE_URL}/rest/v1/daily_entries?select=*&order=created_at.desc`, { headers }),
        fetch(`${SUPABASE_URL}/rest/v1/audit_log?select=*&order=created_at.desc&limit=500`, { headers }),
      ]);
      const [profiles, allEntries, logs] = await Promise.all([profRes.json(), entRes.json(), auditRes.json()]);
      const safeProfiles = Array.isArray(profiles) ? profiles : [];
      const safeLogs = Array.isArray(logs) ? logs : [];
      // Build userId->email from audit log
      const emailMap = {};
      safeLogs.forEach(l => { if (l.user_id && l.email) emailMap[l.user_id] = l.email; });
      const nameMap = {};
      safeProfiles.forEach(p => { if (p.id && p.name) nameMap[p.id] = p.name; });
      // Only show profiles that have a name saved (real clients) — exclude admin and blank rows
      const ADMIN_ID = '8632013e-30f8-46ad-b105-38a902aedcbf';
      const enrichedProfiles = safeProfiles
        .filter(p => p.id && p.id !== ADMIN_ID && p.name)
        .map(p => ({
          ...p,
          email: emailMap[p.id] || null,
          name: p.name,
        }));
      setNameMap(nameMap);
      setUsers(enrichedProfiles);
      setEntries(Array.isArray(allEntries) ? allEntries : []);
      setAuditLog(safeLogs);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const userEntries = selectedUser ? entries.filter(e => e.user_id === selectedUser.id) : [];
  const userAudit = selectedUser ? auditLog.filter(a => a.user_id === selectedUser.id) : [];

  const weightDelta = (userId) => {
    const ue = entries.filter(e => e.user_id === userId && e.morning_weight);
    if (ue.length < 2) return null;
    const sorted = [...ue].sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
    return (sorted[sorted.length-1].morning_weight - sorted[0].morning_weight).toFixed(1);
  };

  const actionColor = (action) => {
    if (action.includes("login")) return "#00cc88";
    if (action.includes("logout")) return MUTED;
    if (action.includes("checkin") || action.includes("submit")) return TEAL;
    if (action.includes("signup")) return AMBER;
    return TEXT;
  };

  const actionIcon = (action) => {
    if (action.includes("login")) return "🔐";
    if (action.includes("logout")) return "🚪";
    if (action.includes("checkin") || action.includes("submit")) return "✏️";
    if (action.includes("signup")) return "🆕";
    return "•";
  };

  if (showSettings) return (
    <AdminSettings session={session} onBack={() => setShowSettings(false)} branding={branding} onBrandingChange={onBrandingChange} />
  );

  if (selectedUser) return (
    <div style={styles.app}>
      <header style={styles.header}>
        <button onClick={() => setSelectedUser(null)}
          style={{ background: "none", border: "none", color: TEAL,
            fontSize: 13, cursor: "pointer", fontWeight: 700,
            fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}>
          ← All Users
        </button>
        <div style={{ fontSize: 13, color: MUTED }}>Admin View</div>
        <button onClick={onSignOut} style={{ background: "none", border: "none",
          color: MUTED, fontSize: 13, cursor: "pointer" }}>Sign Out</button>
      </header>

      <div style={styles.page}>
        {/* User header */}
          <div style={{ ...styles.card, borderColor: `${TEAL}44`,
            background: `linear-gradient(145deg, ${CARD} 0%, #162830 100%)` }}>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: FONT_DISPLAY }}>
            {selectedUser.name || "Unnamed User"}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
            {userEntries.length} check-ins logged
          </div>
          {weightDelta(selectedUser.id) && (
            <div style={{ marginTop: 10, fontSize: 15, fontWeight: 700,
              color: parseFloat(weightDelta(selectedUser.id)) <= 0 ? "#00cc88" : "#ff6b6b" }}>
              {parseFloat(weightDelta(selectedUser.id)) <= 0 ? "▼" : "▲"} {Math.abs(weightDelta(selectedUser.id))} lbs total change
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {selectedUser.start_weight && (
              <div style={{ background: CARD2, borderRadius: 999, padding: "4px 12px",
                fontSize: 11, color: MUTED, border: `1px solid ${BORDER}` }}>
                Start: {selectedUser.start_weight} lbs
              </div>
            )}
            {selectedUser.goal_weight && (
              <div style={{ background: `${TEAL}18`, borderRadius: 999, padding: "4px 12px",
                fontSize: 11, color: TEAL, border: `1px solid ${TEAL}44` }}>
                🎯 Goal: {selectedUser.goal_weight} lbs
              </div>
            )}
            {userEntries[0]?.entry_date && (
              <div style={{ background: CARD2, borderRadius: 999, padding: "4px 12px",
                fontSize: 11, color: MUTED, border: `1px solid ${BORDER}` }}>
                Last check-in: {new Date(userEntries[0].entry_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </div>
            )}
          </div>
        </div>

        {/* Weight chart */}
        {(() => {
          const pts = [...userEntries]
            .filter(e => e.morning_weight && (e.entry_date || e.created_at))
            .sort((a,b) => new Date(a.entry_date || a.created_at) - new Date(b.entry_date || b.created_at));
          if (pts.length < 2) return null;
          const W = 560, H = 120, PX = 28, PY = 14;
          const weights = pts.map(e => parseFloat(e.morning_weight));
          const allVals = [...weights];
          if (selectedUser.goal_weight) allVals.push(parseFloat(selectedUser.goal_weight));
          if (selectedUser.start_weight) allVals.push(parseFloat(selectedUser.start_weight));
          const minW = Math.min(...allVals) - 2;
          const maxW = Math.max(...allVals) + 2;
          const x = i => PX + (i / (pts.length - 1)) * (W - PX * 2);
          const y = v => PY + (1 - (v - minW) / (maxW - minW)) * (H - PY * 2);
          const line = pts.map((e,i) => `${i===0?"M":"L"}${x(i).toFixed(1)},${y(weights[i]).toFixed(1)}`).join(" ");
          const area = `${line} L${x(pts.length-1).toFixed(1)},${(H-PY).toFixed(1)} L${x(0).toFixed(1)},${(H-PY).toFixed(1)} Z`;
          const goalY = selectedUser.goal_weight ? y(parseFloat(selectedUser.goal_weight)) : null;
          const latest = weights[weights.length-1];
          const first = weights[0];
          return (
            <div style={{ ...styles.card, padding: "14px 16px 10px", marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
                Weight Trend
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
                {/* area fill */}
                <defs>
                  <linearGradient id="wfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TEAL} stopOpacity="0.25" />
                    <stop offset="100%" stopColor={TEAL} stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                <path d={area} fill="url(#wfill)" />
                {/* goal line */}
                {goalY !== null && (
                  <>
                    <line x1={PX} y1={goalY.toFixed(1)} x2={W-PX} y2={goalY.toFixed(1)}
                      stroke={CORAL} strokeWidth="1.5" strokeDasharray="5,4" opacity="0.7" />
                    <text x={W-PX+4} y={goalY+4} fontSize="9" fill={CORAL} opacity="0.9">goal</text>
                  </>
                )}
                {/* weight line */}
                <path d={line} fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                {/* dots — first and last only */}
                <circle cx={x(0).toFixed(1)} cy={y(first).toFixed(1)} r="4" fill={TEAL} />
                <circle cx={x(pts.length-1).toFixed(1)} cy={y(latest).toFixed(1)} r="4" fill={TEAL} />
                {/* labels */}
                <text x={x(0).toFixed(1)} y={(y(first)-7).toFixed(1)} textAnchor="middle" fontSize="10" fill={TEXT} opacity="0.8">{first}</text>
                <text x={x(pts.length-1).toFixed(1)} y={(y(latest)-7).toFixed(1)} textAnchor="middle" fontSize="10" fill={TEXT} opacity="0.8">{latest}</text>
                {/* x-axis labels: first and last date */}
                <text x={x(0).toFixed(1)} y={H-1} textAnchor="middle" fontSize="9" fill={MUTED}>
                  {new Date((pts[0].entry_date||pts[0].created_at)+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                </text>
                <text x={x(pts.length-1).toFixed(1)} y={H-1} textAnchor="middle" fontSize="9" fill={MUTED}>
                  {new Date((pts[pts.length-1].entry_date||pts[pts.length-1].created_at)+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                </text>
              </svg>
            </div>
          );
        })()}

        {/* Activity tabs */}
        <div style={{ display: "flex", background: CARD, borderRadius: 999,
          padding: 4, marginBottom: 16, border: `1px solid ${BORDER}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.25)" }}>
          {["entries", "activity"].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ flex: 1, background: activeTab === t ? CORAL : "transparent",
                border: "none", borderRadius: 999, padding: "9px",
                fontSize: 13, fontWeight: 700, fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.04em",
                color: activeTab === t ? "#fff" : MUTED, cursor: "pointer",
                transition: "all 0.25s",
                boxShadow: activeTab === t ? "0 2px 10px rgba(244,97,58,0.35)" : "none" }}>
              {t === "entries" ? "📋 Daily Entries" : "🕐 Activity Log"}
            </button>
          ))}
        </div>

        {/* Daily entries — newest first */}
        {activeTab === "entries" && (
          <div>
            {userEntries.length === 0 && (
              <div style={{ ...styles.card, textAlign: "center", color: MUTED }}>No entries yet</div>
            )}
            {[...userEntries].sort((a,b) => new Date(b.created_at) - new Date(a.created_at)).map((e, i) => (
              <div key={i} style={styles.card}>
                <EntryHeader e={e} />
                {e.ai_response && <AIResponse response={e.ai_response} />}
                {e.followup_qa && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>💬 Client Responses</div>
                    <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6, whiteSpace: "pre-line" }}>{e.followup_qa}</div>
                  </div>
                )}
                {e.followup_response && (
                  <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, background: `linear-gradient(135deg, ${CARD} 0%, rgba(0,180,191,0.08) 100%)`, border: `1px solid ${TEAL}44` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.12em", marginBottom: 6, textTransform: "uppercase" }}>💚 Coach's Closing Note</div>
                    <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6 }}>{e.followup_response}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Activity log — newest first */}
        {activeTab === "activity" && (
          <div>
            {userAudit.length === 0 && (
              <div style={{ ...styles.card, textAlign: "center", color: MUTED }}>No activity logged yet</div>
            )}
            {[...userAudit].sort((a,b) => new Date(b.created_at) - new Date(a.created_at)).map((log, i) => (
              <div key={i} style={{ ...styles.card, padding: "12px 16px", marginBottom: 8,
                display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ fontSize: 18, marginTop: 2 }}>{actionIcon(log.action)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: actionColor(log.action), textTransform: "capitalize" }}>
                    {log.action.replace(/_/g, " ")}
                  </div>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                    {new Date(log.created_at).toLocaleString("en-US",
                      { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCount = entries.filter(e => e.entry_date === todayStr || e.created_at?.slice(0,10) === todayStr).length;

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={branding.logoUrl} alt="" style={{ height: 56 }} />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={load} style={{ background: "none", border: `1px solid ${BORDER}`,
            color: MUTED, fontSize: 11, cursor: "pointer", borderRadius: 999, padding: "5px 12px",
            fontFamily: "'Montserrat', sans-serif" }}>↻ Refresh</button>
          <button onClick={() => setShowSettings(true)} style={{ background: "none", border: `1px solid ${BORDER}`,
            color: MUTED, fontSize: 11, cursor: "pointer", borderRadius: 999, padding: "5px 12px",
            fontFamily: "'Montserrat', sans-serif" }}>⚙️ Settings</button>
          <button onClick={onSignOut} style={{ background: "none", border: "none",
            color: MUTED, fontSize: 13, cursor: "pointer" }}>Sign Out</button>
        </div>
      </header>

      <div style={{ ...styles.page, maxWidth: viewMode === "table" ? 1200 : 480 }}>
        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[["Users", users.length, TEAL], ["Total Check-ins", entries.length, CORAL], ["Today", todayCount, AMBER]].map(([label, val, color]) => (
            <div key={label} style={{ ...styles.card, textAlign: "center", padding: "14px 8px", marginBottom: 0 }}>
              <div style={{ fontSize: 28, fontWeight: 900, color, fontFamily: FONT_DISPLAY }}>{val}</div>
              <div style={{ fontSize: 9, color: MUTED, marginTop: 3, textTransform: "uppercase",
                letterSpacing: "0.12em", fontFamily: "'Montserrat', sans-serif" }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Main tab toggle + view toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, display: "flex", background: CARD, borderRadius: 999, padding: 4, border: `1px solid ${BORDER}` }}>
          {[["users", "👥 Users"], ["activity", "🕐 Activity"]].map(([tab, label]) => (
            <button key={tab} onClick={() => setMainTab(tab)}
              style={{ flex: 1, background: mainTab === tab ? TEAL : "transparent",
                border: "none", borderRadius: 999, padding: "9px", fontSize: 13,
                fontWeight: 700, fontFamily: "'Montserrat', sans-serif",
                color: mainTab === tab ? "#000" : MUTED, cursor: "pointer", transition: "all 0.25s" }}>
              {label}
            </button>
          ))}
          </div>
          {/* View toggle — controls width for both tabs */}
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {[["table", "⊞", "Table"], ["cards", "▤", "Cards"]].map(([mode, icon, label]) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{ background: viewMode === mode ? TEAL : CARD, border: `1px solid ${viewMode === mode ? TEAL : BORDER}`,
                  borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700,
                  fontFamily: "'Montserrat', sans-serif",
                  color: viewMode === mode ? "#000" : MUTED, cursor: "pointer", transition: "all 0.2s",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* USERS TAB */}
        {mainTab === "users" && (
          <div>
            {/* Add Client */}
            {addClientSuccess && (
              <div style={{ background: "rgba(0,204,136,0.1)", border: "1px solid rgba(0,204,136,0.3)",
                borderRadius: 12, padding: "12px 16px", marginBottom: 12, color: "#00cc88", fontSize: 13 }}>
                ✓ {addClientSuccess}
              </div>
            )}
            {!showAddClient ? (
              <button onClick={() => { setShowAddClient(true); setAddClientSuccess(""); }}
                style={{ ...styles.btn(), marginBottom: 16 }}>+ Add Client</button>
            ) : (
              <div style={{ ...styles.card, borderColor: `${TEAL}44`, marginBottom: 16 }}>
                <div style={styles.cardTitle}>New Client</div>
                <input placeholder="Full name" value={addClientForm.name} autoComplete="off"
                  onChange={e => setAddClientForm({ ...addClientForm, name: e.target.value })}
                  style={{ ...styles.input, marginBottom: 8 }} />
                <input placeholder="Email address" type="email" value={addClientForm.email} autoComplete="off"
                  onChange={e => setAddClientForm({ ...addClientForm, email: e.target.value })}
                  style={{ ...styles.input, marginBottom: 8 }} />
                <input placeholder="Temporary password" type="password" value={addClientForm.password} autoComplete="new-password"
                  onChange={e => setAddClientForm({ ...addClientForm, password: e.target.value })}
                  style={{ ...styles.input, marginBottom: 8 }} />
                {addClientError && (
                  <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 8 }}>{addClientError}</div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={handleAddClient} disabled={addClientLoading}
                    style={{ ...styles.btn(), flex: 2, padding: "10px", fontSize: 13, marginTop: 0 }}>
                    {addClientLoading ? "Creating..." : "Create Client"}
                  </button>
                  <button onClick={() => { setShowAddClient(false); setAddClientError(""); setAddClientForm({ name: "", email: "", password: "" }); }}
                    style={{ ...styles.btn("secondary"), flex: 1, padding: "10px", fontSize: 13, marginTop: 0 }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {loading && <div style={{ color: MUTED, textAlign: "center", padding: 20 }}>Loading...</div>}
            {!loading && users.length === 0 && (
              <div style={{ ...styles.card, textAlign: "center", color: MUTED }}>No users yet</div>
            )}

            {/* TABLE VIEW */}
            {!loading && users.length > 0 && viewMode === "table" && (
              <div style={{ overflowX: "auto", borderRadius: 14, border: `1px solid ${BORDER}`, WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: CARD2, borderBottom: `1px solid ${BORDER}` }}>
                      {["Client", "Check-ins", "Last Check-in", "Start", "Current", "Goal", "Change", "Trend", ""].map((h, i) => (
                        <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10,
                          fontWeight: 700, color: MUTED, letterSpacing: "0.08em", textTransform: "uppercase",
                          whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => {
                      const delta = weightDelta(u.id);
                      const ue = entries.filter(e => e.user_id === u.id);
                      const lastEntry = ue[0];
                      const lastEntryDate = lastEntry?.entry_date || lastEntry?.created_at?.slice(0, 10);
                      const todayDate = new Date().toLocaleDateString("en-CA");
                      const daysSince = lastEntryDate ? Math.round((new Date(todayDate) - new Date(lastEntryDate)) / 86400000) : null;
                      const nudgeColor = daysSince === null ? "#ff6b6b" : daysSince >= 7 ? "#ff6b6b" : daysSince >= 3 ? AMBER : "#00cc88";
                      const nudgeLabel = daysSince === null ? "Never" : daysSince === 0 ? "Today" : `${daysSince}d ago`;
                      const sortedUe = [...ue].filter(e => e.morning_weight).sort((a,b) => new Date(a.entry_date||a.created_at) - new Date(b.entry_date||b.created_at));
                      const currentWeight = sortedUe.length ? sortedUe[sortedUe.length-1].morning_weight : null;
                      const wpts = sortedUe.slice(-10);
                      const sparkline = (() => {
                        if (wpts.length < 2) return null;
                        const SW = 80, SH = 24;
                        const vals = wpts.map(e => parseFloat(e.morning_weight));
                        const mn = Math.min(...vals), mx = Math.max(...vals);
                        const sx = j => (j / (wpts.length - 1)) * SW;
                        const sy = v => SH - 2 - ((mx === mn ? 0.5 : (v - mn) / (mx - mn)) * (SH - 4));
                        const sline = vals.map((v,j) => `${j===0?"M":"L"}${sx(j).toFixed(1)},${sy(v).toFixed(1)}`).join(" ");
                        const sc = vals[vals.length-1] - vals[0] <= 0 ? "#00cc88" : "#ff6b6b";
                        return <svg viewBox={`0 0 ${SW} ${SH}`} style={{ width: 80, height: 24, display: "block", overflow: "visible" }}>
                          <path d={sline} fill="none" stroke={sc} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
                          <circle cx={sx(vals.length-1).toFixed(1)} cy={sy(vals[vals.length-1]).toFixed(1)} r="2.5" fill={sc} />
                        </svg>;
                      })();
                      return (
                        <tr key={i} onClick={() => { setSelectedUser(u); setActiveTab("entries"); logAudit(session.user?.id, session.user?.email, "admin_viewed_user", { viewed_user_id: u.id, viewed_user_email: u.email }, session.access_token); }}
                          style={{ borderBottom: `1px solid ${BORDER}`, cursor: "pointer", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = CARD2}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            <div style={{ fontWeight: 600, color: TEXT }}>{u.name || u.email?.split('@')[0] || "Unnamed"}</div>
                            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{u.email}</div>
                          </td>
                          <td style={{ padding: "12px 14px", color: MUTED, textAlign: "center" }}>{ue.length}</td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: nudgeColor, background: `${nudgeColor}18`,
                              border: `1px solid ${nudgeColor}44`, borderRadius: 999, padding: "2px 8px" }}>
                              {nudgeLabel}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", color: MUTED, whiteSpace: "nowrap" }}>{u.start_weight ? `${u.start_weight} lbs` : "—"}</td>
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: TEXT, whiteSpace: "nowrap" }}>{currentWeight ? `${currentWeight} lbs` : "—"}</td>
                          <td style={{ padding: "12px 14px", color: TEAL, whiteSpace: "nowrap" }}>{u.goal_weight ? `${u.goal_weight} lbs` : "—"}</td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            {delta ? (
                              <span style={{ fontWeight: 800, color: parseFloat(delta) <= 0 ? "#00cc88" : "#ff6b6b" }}>
                                {parseFloat(delta) <= 0 ? "▼" : "▲"} {Math.abs(delta)} lbs
                              </span>
                            ) : "—"}
                          </td>
                          <td style={{ padding: "12px 14px" }}>{sparkline}</td>
                          <td style={{ padding: "12px 14px", color: TEAL, fontWeight: 700 }}>→</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* CARD VIEW */}
            {!loading && users.length > 0 && viewMode === "cards" && users.map((u, i) => {
              const delta = weightDelta(u.id);
              const ue = entries.filter(e => e.user_id === u.id);
              const lastEntry = ue[0];
              const lastDate = lastEntry ? new Date(lastEntry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
              const lastEntryDate = lastEntry?.entry_date || lastEntry?.created_at?.slice(0, 10);
              const todayDate = new Date().toLocaleDateString("en-CA");
              const daysSince = lastEntryDate ? Math.round((new Date(todayDate) - new Date(lastEntryDate)) / 86400000) : null;
              const nudgeColor = daysSince === null ? "#ff6b6b" : daysSince >= 7 ? "#ff6b6b" : daysSince >= 3 ? AMBER : "#00cc88";
              const nudgeLabel = daysSince === null ? "Never checked in" : daysSince === 0 ? "Checked in today" : `${daysSince}d since last check-in`;
              return (
                <div key={i} style={{ ...styles.card, cursor: "pointer", transition: "border-color 0.2s",
                  borderColor: daysSince === null || daysSince >= 7 ? "rgba(255,107,107,0.3)" : daysSince >= 3 ? "rgba(255,180,0,0.3)" : BORDER }}
                  onClick={() => { setSelectedUser(u); setActiveTab("entries"); logAudit(session.user?.id, session.user?.email, "admin_viewed_user", { viewed_user_id: u.id, viewed_user_email: u.email }, session.access_token); }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{u.name || u.email?.split('@')[0] || "Unnamed User"}</div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{u.email}</div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>
                        {ue.length} check-in{ue.length !== 1 ? "s" : ""}
                        {lastDate ? ` · Last: ${lastDate}` : ""}
                        {u.goal_weight ? ` · Goal: ${u.goal_weight} lbs` : ""}
                        {u.start_weight ? ` · Start: ${u.start_weight} lbs` : ""}
                      </div>
                      {(() => {
                        const wpts = [...ue]
                          .filter(e => e.morning_weight)
                          .sort((a,b) => new Date(a.entry_date||a.created_at) - new Date(b.entry_date||b.created_at))
                          .slice(-10);
                        if (wpts.length < 2) return null;
                        const SW = 80, SH = 24;
                        const vals = wpts.map(e => parseFloat(e.morning_weight));
                        const mn = Math.min(...vals), mx = Math.max(...vals);
                        const sx = i => (i / (wpts.length - 1)) * SW;
                        const sy = v => SH - 2 - ((mx === mn ? 0.5 : (v - mn) / (mx - mn)) * (SH - 4));
                        const sline = vals.map((v,i) => `${i===0?"M":"L"}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join(" ");
                        const trend = vals[vals.length-1] - vals[0];
                        const sc = trend <= 0 ? "#00cc88" : "#ff6b6b";
                        return (
                          <svg viewBox={`0 0 ${SW} ${SH}`} style={{ width: 80, height: 24, display: "block", marginTop: 6, overflow: "visible" }}>
                            <path d={sline} fill="none" stroke={sc} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
                            <circle cx={sx(vals.length-1).toFixed(1)} cy={sy(vals[vals.length-1]).toFixed(1)} r="2.5" fill={sc} />
                          </svg>
                        );
                      })()}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: nudgeColor, background: `${nudgeColor}18`,
                        border: `1px solid ${nudgeColor}44`, borderRadius: 999, padding: "2px 8px",
                        letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
                        {nudgeLabel}
                      </div>
                      {delta && (
                        <div style={{ fontSize: 13, fontWeight: 800,
                          color: parseFloat(delta) <= 0 ? "#00cc88" : "#ff6b6b" }}>
                          {parseFloat(delta) <= 0 ? "▼" : "▲"} {Math.abs(delta)} lbs
                        </div>
                      )}
                      <div style={{ fontSize: 16, color: TEAL, fontWeight: 700 }}>→</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ACTIVITY TAB */}
        {mainTab === "activity" && (() => {
          const todayDate = new Date().toLocaleDateString("en-CA");
          const checkedInToday = new Set(entries.filter(e => e.entry_date === todayDate).map(e => e.user_id));
          const totalClients = users.length;

          const relTime = (dateStr) => {
            const mins = Math.floor((Date.now() - new Date(dateStr)) / 60000);
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            const days = Math.floor(hrs / 24);
            if (days === 1) return "yesterday";
            return `${days}d ago`;
          };

          const clientName = (log) => nameMap[log.user_id] || (log.email ? log.email.split('@')[0].split('+')[0].charAt(0).toUpperCase() + log.email.split('@')[0].split('+')[0].slice(1) : "Someone");

          const actLabel = (log) => {
            switch(log.action) {
              case "checkin_submitted": return "checked in";
              case "followup_submitted": return "answered follow-up questions";
              case "goal_updated": return `updated goal → ${log.metadata?.goal_weight} lbs`;
              case "user_signup": return "joined";
              case "user_login": return null;
              case "user_logout": return null;
              case "admin_viewed_user": return null;
              default: return log.action.replace(/_/g, " ");
            }
          };

          const isHighlight = (action) => ["checkin_submitted", "followup_submitted", "goal_updated", "user_signup"].includes(action);
          const feed = auditLog.filter(l => actLabel(l) !== null);

          return (
            <div>
              {/* Today's summary */}
              <div style={{ ...styles.card, background: `linear-gradient(135deg, ${CARD} 0%, #162830 100%)`,
                borderColor: `${TEAL}44`, marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: TEAL, letterSpacing: "0.12em",
                  textTransform: "uppercase", fontFamily: "'Montserrat', sans-serif", marginBottom: 6 }}>Today's Summary</div>
                <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "'Montserrat', sans-serif", color: TEXT }}>
                  {checkedInToday.size} <span style={{ fontSize: 14, color: MUTED, fontWeight: 400 }}>of {totalClients} clients checked in</span>
                </div>
                {totalClients > 0 && (
                  <div style={{ marginTop: 10, height: 6, borderRadius: 999, background: CARD2, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 999, background: TEAL,
                      width: `${(checkedInToday.size / totalClients) * 100}%`, transition: "width 0.5s" }} />
                  </div>
                )}
              </div>

              {/* Activity feed */}
              {feed.length === 0 && (
                <div style={{ ...styles.card, textAlign: "center", color: MUTED }}>No activity yet</div>
              )}
              {feed.map((log, i) => {
                const highlight = isHighlight(log.action);
                return (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12,
                    padding: "10px 16px", marginBottom: 4, borderRadius: 12,
                    background: highlight ? CARD : "transparent",
                    border: highlight ? `1px solid ${BORDER}` : "none",
                    opacity: highlight ? 1 : 0.6 }}>
                    <div style={{ fontSize: 16, marginTop: 1, flexShrink: 0 }}>{actionIcon(log.action)}</div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: highlight ? 700 : 400, color: highlight ? TEXT : MUTED }}>
                        {clientName(log)}
                      </span>
                      <span style={{ fontSize: 13, color: MUTED }}> {actLabel(log)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, flexShrink: 0, marginTop: 2 }}>{relTime(log.created_at)}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}


function AdminSettings({ session, onBack, branding = { appName: "Life Alignment Coach", tagline: "Dripped. Not Sipped.", coachName: "Tina", logoUrl: LOGO_URL }, onBrandingChange }) {
  const [settingsTab, setSettingsTab] = useState("users");
  const [provider, setProvider] = useState("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-20250514");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [brandForm, setBrandForm] = useState({ appName: branding.appName, tagline: branding.tagline, coachName: branding.coachName, logoUrl: branding.logoUrl || LOGO_URL });
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);
  // Users tab
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");

  const headers = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };

  const PROVIDERS = [
    { id: "anthropic", name: "Anthropic Claude", models: ["claude-sonnet-4-20250514", "claude-opus-4-5", "claude-haiku-4-5-20251001"], color: TEAL },
    { id: "openai", name: "OpenAI ChatGPT", models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo"], color: "#10a37f" },
    { id: "gemini", name: "Google Gemini", models: ["gemini-1.5-pro", "gemini-1.5-flash", "gemini-2.0-flash"], color: "#4285f4" },
  ];

  useEffect(() => {
    // Load from localStorage
    const p = localStorage.getItem("sf_ai_provider");
    const k = localStorage.getItem("sf_ai_key");
    const m = localStorage.getItem("sf_ai_model");
    if (p) setProvider(p);
    if (k) setApiKey(k);
    if (m) setModel(m);
    setLoading(false);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      localStorage.setItem("sf_ai_provider", provider);
      localStorage.setItem("sf_ai_key", apiKey);
      localStorage.setItem("sf_ai_model", model);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  const saveBranding = async () => {
    setBrandSaving(true);
    try {
      const upsert = async (key, value) => {
        // Try UPDATE first (row likely exists), fall back to INSERT
        const hdrsBase = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" };
        const patch = await fetch(`${SUPABASE_URL}/rest/v1/app_settings?key=eq.${key}`, {
          method: "PATCH", headers: { ...hdrsBase, Prefer: "return=minimal" }, body: JSON.stringify({ value }),
        });
        if (patch.status === 404 || patch.headers.get("content-range") === "*/0") {
          await fetch(`${SUPABASE_URL}/rest/v1/app_settings`, {
            method: "POST", headers: hdrsBase, body: JSON.stringify({ key, value }),
          });
        }
        return patch;
      };
      const results = await Promise.all([
        upsert("brand_app_name", brandForm.appName),
        upsert("brand_tagline", brandForm.tagline),
        upsert("brand_coach_name", brandForm.coachName),
        upsert("brand_logo_url", brandForm.logoUrl),
      ]);
      const failed = results.find(r => !r.ok && r.status !== 204 && r.status !== 200);
      if (failed) {
        const body = await failed.text();
        console.error("Branding save failed", failed.status, body);
        alert(`Save failed (${failed.status}): ${body}`);
      } else {
        if (onBrandingChange) onBrandingChange({ appName: brandForm.appName, tagline: brandForm.tagline, coachName: brandForm.coachName, logoUrl: brandForm.logoUrl });
        setBrandSaved(true);
        setTimeout(() => setBrandSaved(false), 3000);
      }
    } catch(e) { console.error(e); alert("Network error saving branding."); }
    setBrandSaving(false);
  };

  const currentProvider = PROVIDERS.find(p => p.id === provider);

  const handleAddClient = async () => {
    if (!addForm.name || !addForm.email || !addForm.password) { setAddError("All fields required."); return; }
    setAddLoading(true); setAddError(""); setAddSuccess("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/invite-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(addForm),
      });
      const data = await res.json();
      if (!res.ok) { setAddError(data.error || "Failed to create client."); }
      else { setAddSuccess(`${addForm.name} created successfully.`); setAddForm({ name: "", email: "", password: "" }); }
    } catch(e) { setAddError("Network error."); }
    setAddLoading(false);
  };

  const SETTINGS_TABS = [
    { id: "users", label: "👤 Add Client" },
    { id: "branding", label: "🎨 Branding" },
    { id: "ai", label: "🤖 AI" },
  ];

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <button onClick={onBack} style={{ background: "none", border: "none",
          color: TEAL, fontSize: 13, cursor: "pointer", fontWeight: 700,
          fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}>
          ← Admin
        </button>
        <div style={{ fontSize: 13, fontWeight: 800, color: TEAL, fontFamily: FONT_DISPLAY }}>
          Settings
        </div>
        <div style={{ width: 60 }} />
      </header>

      <div style={styles.page}>
        {/* Settings tab bar */}
        <div style={{ display: "flex", background: CARD, borderRadius: 999, padding: 4,
          marginBottom: 20, border: `1px solid ${BORDER}`, boxShadow: "0 2px 12px rgba(0,0,0,0.25)" }}>
          {SETTINGS_TABS.map(t => (
            <button key={t.id} onClick={() => setSettingsTab(t.id)}
              style={{ flex: 1, background: settingsTab === t.id ? CORAL : "transparent",
                border: "none", borderRadius: 999, padding: "9px 4px",
                fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.03em", color: settingsTab === t.id ? "#fff" : MUTED,
                cursor: "pointer", transition: "all 0.2s",
                boxShadow: settingsTab === t.id ? "0 2px 10px rgba(244,97,58,0.35)" : "none" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* BRANDING TAB */}
        {settingsTab === "branding" && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Branding</div>
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 16, lineHeight: 1.6 }}>
              Sets the app name, tagline, and coach name shown to all users on the login screen and throughout the app.
            </div>
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Logo</label>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, marginTop: 6 }}>
              {brandForm.logoUrl && (
                <img src={brandForm.logoUrl} alt="" style={{ height: 48, borderRadius: 8, background: CARD2, padding: 4, flexShrink: 0 }} onError={e => e.target.style.display="none"} />
              )}
              <div style={{ flex: 1 }}>
                <input type="file" accept="image/*" id="logo-upload" style={{ display: "none" }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => setBrandForm(f => ({ ...f, logoUrl: ev.target.result }));
                    reader.readAsDataURL(file);
                  }} />
                <label htmlFor="logo-upload"
                  style={{ display: "inline-block", background: CARD2, border: `1px solid ${BORDER}`,
                    borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600,
                    color: TEAL, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}>
                  📁 Choose Image File
                </label>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>PNG, JPG, SVG — any size, stored securely</div>
              </div>
            </div>
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>App Name</label>
            <input value={brandForm.appName} onChange={e => setBrandForm({ ...brandForm, appName: e.target.value })}
              placeholder="Life Alignment Coach" style={{ ...styles.input, marginBottom: 14, marginTop: 4 }} />
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Tagline</label>
            <input value={brandForm.tagline} onChange={e => setBrandForm({ ...brandForm, tagline: e.target.value })}
              placeholder="Dripped. Not Sipped." style={{ ...styles.input, marginBottom: 14, marginTop: 4 }} />
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Coach Name</label>
            <input value={brandForm.coachName} onChange={e => setBrandForm({ ...brandForm, coachName: e.target.value })}
              placeholder="Tina" style={{ ...styles.input, marginBottom: 20, marginTop: 4 }} />
            <button onClick={saveBranding} disabled={brandSaving} style={{ ...styles.btn(), marginTop: 0 }}>
              {brandSaving ? "Saving..." : brandSaved ? "✓ Saved!" : "Save Branding"}
            </button>
          </div>
        )}

        {/* AI TAB */}
        {settingsTab === "ai" && (
          loading ? <div style={{ color: MUTED, textAlign: "center", padding: 40 }}>Loading...</div> : (
            <>
              <div style={styles.card}>
                <div style={styles.cardTitle}>Choose AI Provider</div>
                <div style={{ display: "grid", gap: 10 }}>
                  {PROVIDERS.map(p => (
                    <div key={p.id} onClick={() => { setProvider(p.id); setModel(p.models[0]); }}
                      style={{ background: provider === p.id ? `${p.color}22` : CARD2,
                        border: `2px solid ${provider === p.id ? p.color : BORDER}`,
                        borderRadius: 14, padding: "14px 16px", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 12,
                        boxShadow: provider === p.id ? `0 4px 16px ${p.color}33` : "none",
                        transition: "all 0.25s" }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%",
                        background: provider === p.id ? p.color : MUTED,
                        boxShadow: provider === p.id ? `0 0 8px ${p.color}` : "none",
                        transition: "all 0.2s", flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: provider === p.id ? p.color : TEXT }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{p.models.length} models available</div>
                      </div>
                      {provider === p.id && <div style={{ marginLeft: "auto", color: p.color, fontSize: 18 }}>✓</div>}
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.card}>
                <div style={styles.cardTitle}>Select Model</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {currentProvider?.models.map(m => (
                    <div key={m} onClick={() => setModel(m)}
                      style={{ background: model === m ? TEAL_GLOW : CARD2,
                        border: `1px solid ${model === m ? TEAL : BORDER}`,
                        borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        transition: "all 0.25s" }}>
                      <span style={{ fontSize: 13, color: model === m ? TEAL : MUTED, fontFamily: "monospace" }}>{m}</span>
                      {model === m && <span style={{ color: TEAL }}>✓</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.card}>
                <div style={styles.cardTitle}>API Key</div>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 12, lineHeight: 1.6 }}>
                  {provider === "anthropic" && "Get your key at console.anthropic.com/settings/keys"}
                  {provider === "openai" && "Get your key at platform.openai.com/api-keys"}
                  {provider === "gemini" && "Get your key at aistudio.google.com/app/apikey"}
                </div>
                <div style={{ position: "relative" }}>
                  <input placeholder={`Paste your ${currentProvider?.name} API key here`}
                    value={apiKey} onChange={e => setApiKey(e.target.value)}
                    type={showKey ? "text" : "password"}
                    style={{ ...styles.input, marginBottom: 0, paddingRight: 48, fontFamily: "monospace", fontSize: 13 }} />
                  <button onClick={() => setShowKey(!showKey)} type="button"
                    style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer", color: MUTED, fontSize: 16 }}>
                    {showKey ? "🙈" : "👁️"}
                  </button>
                </div>
                {apiKey && <div style={{ marginTop: 8, fontSize: 11, color: "#00cc88" }}>✓ Key entered — {apiKey.slice(0,8)}...{apiKey.slice(-4)}</div>}
              </div>

              <button onClick={save} disabled={saving || !apiKey}
                style={{ ...styles.btn(), opacity: !apiKey ? 0.45 : 1, cursor: !apiKey ? "not-allowed" : "pointer" }}>
                {saving ? "Saving..." : saved ? "✓ Saved!" : "Save AI Settings"}
              </button>
              {saved && <div style={{ textAlign: "center", color: "#00cc88", fontSize: 13, marginTop: 12, fontWeight: 700 }}>
                AI provider updated — users will now use {currentProvider?.name}
              </div>}
            </>
          )
        )}

        {/* ADD CLIENT TAB */}
        {settingsTab === "users" && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Add New Client</div>
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 16, lineHeight: 1.6 }}>
              Creates a new client account. They can log in immediately with the temporary password you set.
            </div>
            {addSuccess && (
              <div style={{ background: "rgba(0,204,136,0.1)", border: "1px solid rgba(0,204,136,0.3)",
                borderRadius: 10, padding: "10px 14px", marginBottom: 14, color: "#00cc88", fontSize: 13 }}>
                ✓ {addSuccess}
              </div>
            )}
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Full Name</label>
            <input placeholder="Jane Smith" value={addForm.name} autoComplete="off"
              onChange={e => setAddForm({ ...addForm, name: e.target.value })}
              style={{ ...styles.input, marginBottom: 14, marginTop: 4 }} />
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Email Address</label>
            <input placeholder="jane@email.com" type="email" value={addForm.email} autoComplete="off"
              onChange={e => setAddForm({ ...addForm, email: e.target.value })}
              style={{ ...styles.input, marginBottom: 14, marginTop: 4 }} />
            <label style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Temporary Password</label>
            <input placeholder="Temporary password" type="password" value={addForm.password} autoComplete="new-password"
              onChange={e => setAddForm({ ...addForm, password: e.target.value })}
              style={{ ...styles.input, marginBottom: 14, marginTop: 4 }} />
            {addError && <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 12 }}>{addError}</div>}
            <button onClick={handleAddClient} disabled={addLoading} style={{ ...styles.btn(), marginTop: 0 }}>
              {addLoading ? "Creating..." : "Create Client"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================

export default function App() {
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [branding, setBranding] = useState({ appName: "Life Alignment Coach", tagline: "Dripped. Not Sipped.", coachName: "Tina", logoUrl: LOGO_URL });
  const [tab, setTab] = useState("dashboard");
  const currentEntryIdRef = useRef(null); // stores real Supabase ID for follow-up PATCH
  const [entries, setEntries] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openEntry, setOpenEntry] = useState(null);

  // Restore saved session on app load — "Keep me signed in"
  useEffect(() => {
    const saved = localStorage.getItem("sf_session");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.access_token) {
          setSession(parsed);
          loadBranding(parsed.access_token);
          if (parsed.user?.id) {
            // Restore entries
            fetch(
              SUPABASE_URL + "/rest/v1/daily_entries?user_id=eq." + parsed.user.id + "&order=created_at.desc",
              { headers: { apikey: SUPABASE_ANON_KEY, Authorization: "Bearer " + parsed.access_token, "Content-Type": "application/json" } }
            ).then(r => r.json()).then(data => { if (Array.isArray(data)) setEntries(data); }).catch(e => console.error("Entry restore failed", e));
            // Restore profile — is_admin flag drives admin routing
            fetch(
              SUPABASE_URL + "/rest/v1/profiles?id=eq." + parsed.user.id + "&select=*",
              { headers: { apikey: SUPABASE_ANON_KEY, Authorization: "Bearer " + parsed.access_token } }
            ).then(r => r.json()).then(rows => {
              if (Array.isArray(rows) && rows[0]) {
                const p = rows[0];
                if (p.is_admin) {
                  setIsAdmin(true);
                  logAudit(parsed.user?.id, parsed.user?.email, "user_login", { admin: true }, parsed.access_token);
                } else {
                  setUserProfile({ name: p.name || (parsed.user?.email?.split('@')[0].split('+')[0].charAt(0).toUpperCase() + (parsed.user?.email?.split('@')[0].split('+')[0] || '').slice(1)), start_weight: parseFloat(p.start_weight) || null, goal_weight: parseFloat(p.goal_weight) || null });
                }
              }
            }).catch(() => {});
          }
        }
      } catch { localStorage.removeItem("sf_session"); }
    }
  }, []);

  // Blank user — never hardcode Tina's data
  const demoUser = {
    name: "",
    email: "",
    start_weight: null,
    goal_weight: null,
  };

  const loadBranding = async (token) => {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/app_settings?key=in.(brand_app_name,brand_tagline,brand_coach_name,brand_logo_url)&select=key,value`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` } }
      );
      const rows = await res.json();
      if (Array.isArray(rows) && rows.length) {
        const map = Object.fromEntries(rows.map(r => [r.key, r.value]));
        setBranding({
          appName: map.brand_app_name || "Life Alignment Coach",
          tagline: map.brand_tagline || "Dripped. Not Sipped.",
          coachName: map.brand_coach_name || "Tina",
          logoUrl: map.brand_logo_url || LOGO_URL,
        });
      }
    } catch(e) { /* keep defaults */ }
  };

  const fetchEntries = async (accessToken, userId) => {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/daily_entries?user_id=eq.${userId}&order=created_at.desc`,
        {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await res.json();
      if (Array.isArray(data)) setEntries(data);
    } catch (e) {
      console.error("Failed to load entries", e);
    }
  };

  const handleAuth = (data) => {
    setSession(data);
    if (data.isNew) setTab("checkin");
    if (data.access_token) loadBranding(data.access_token);
    if (data.access_token && data.user?.id) {
      // Fetch profile — is_admin flag drives admin routing
      fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${data.user.id}&select=*`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${data.access_token}` }
      }).then(r => r.json()).then(rows => {
        if (Array.isArray(rows) && rows[0]) {
          const p = rows[0];
          if (p.is_admin) {
            setIsAdmin(true);
            logAudit(data.user?.id, data.user?.email, "user_login", { admin: true }, data.access_token);
          } else {
            setUserProfile({ name: p.name || (data.user?.email?.split('@')[0].split('+')[0].charAt(0).toUpperCase() + (data.user?.email?.split('@')[0].split('+')[0] || '').slice(1)), start_weight: parseFloat(p.start_weight) || null, goal_weight: parseFloat(p.goal_weight) || null });
            logAudit(data.user?.id, data.user?.email, "user_login", {}, data.access_token);
            fetchEntries(data.access_token, data.user.id);
          }
        } else {
          if (data.profile?.name) setUserProfile(data.profile);
          else setUserProfile({ name: (data.user?.email?.split('@')[0].split('+')[0].charAt(0).toUpperCase() + (data.user?.email?.split('@')[0].split('+')[0] || '').slice(1)), start_weight: null, goal_weight: null });
          logAudit(data.user?.id, data.user?.email, "user_login", {}, data.access_token);
          fetchEntries(data.access_token, data.user.id);
        }
      }).catch(() => {});
    }
  };

  const handleFollowUpSubmit = async ({ qa, closing }) => {
    if (!session?.access_token) return;
    const entryId = currentEntryIdRef.current || entries[0]?.id;
    if (!entryId) { console.error("No entry ID for follow-up save"); return; }
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/daily_entries?id=eq.${entryId}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ followup_qa: qa, followup_response: closing }),
      });
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, followup_qa: qa, followup_response: closing } : e));
      logAudit(session.user?.id, session.user?.email, "followup_submitted", { entry_id: entryId }, session.access_token);
    } catch(e) { console.error("Failed to save follow-up", e); }
  };

  const handleHistoryFollowUp = async (entryId, { qa, closing }) => {
    if (!session?.access_token) return;
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/daily_entries?id=eq.${entryId}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ followup_qa: qa, followup_response: closing }),
      });
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, followup_qa: qa, followup_response: closing } : e));
      logAudit(session.user?.id, session.user?.email, "followup_submitted", { entry_id: entryId }, session.access_token);
    } catch(e) { console.error("Failed to save history follow-up", e); }
  };

  const handleCheckInSubmit = async (entry) => {
    const now = new Date().toISOString();
    const newEntry = { ...entry, created_at: now };
    setEntries(prev => [newEntry, ...prev]);
    logAudit(session.user?.id, session.user?.email, "checkin_submitted", { date: now.slice(0, 10) }, session.access_token);
    // Save to Supabase — use return=representation to get real ID back
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/daily_entries`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          Prefer: "return=representation",
        },
        body: JSON.stringify({
          user_id: session.user?.id,
          entry_date: new Date().toISOString().slice(0, 10),
          morning_weight: entry.weight || null,
          sleep_hours: entry.sleep || null,
          energy_level: entry.energy || null,
          mood_level: entry.mood || null,
          stress_level: entry.stress || null,
          workout_done: entry.workout || false,
          notes: entry.notes || null,
          ai_response: entry.ai_response || null,
          created_at: now,
        }),
      });
      const saved = await res.json();
      if (Array.isArray(saved) && saved[0]?.id) {
        currentEntryIdRef.current = saved[0].id; // store immediately in ref — no async delay
        setEntries(prev => prev.map((e, i) => i === 0 ? { ...e, id: saved[0].id } : e));

        const entryId = saved[0].id;
        const userId = session.user?.id;
        const authHeaders = {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        };

        if (entry.meds?.length) {
          fetch(`${SUPABASE_URL}/rest/v1/medications`, {
            method: "POST", headers: authHeaders,
            body: JSON.stringify(entry.meds.map(m => ({
              entry_id: entryId, user_id: userId,
              name: m.name, amount: m.amount || null, time_taken: m.time || null,
            }))),
          }).then(r => { if (!r.ok) r.text().then(b => console.error("[meds] Save failed:", r.status, b)); })
            .catch(e => console.error("[meds] Network error:", e));
        }

        if (entry.supps?.length) {
          fetch(`${SUPABASE_URL}/rest/v1/supplements`, {
            method: "POST", headers: authHeaders,
            body: JSON.stringify(entry.supps.map(s => ({
              entry_id: entryId, user_id: userId,
              name: s.name, amount: s.amount || null, time_taken: s.time || null,
            }))),
          }).then(r => { if (!r.ok) r.text().then(b => console.error("[supps] Save failed:", r.status, b)); })
            .catch(e => console.error("[supps] Network error:", e));
        }

        if (entry.foods?.length) {
          fetch(`${SUPABASE_URL}/rest/v1/food_log`, {
            method: "POST", headers: authHeaders,
            body: JSON.stringify(entry.foods.map(f => ({
              entry_id: entryId, user_id: userId,
              name: f.name, time_eaten: f.time || null, notes: f.notes || null,
            }))),
          }).then(r => { if (!r.ok) r.text().then(b => console.error("[food] Save failed:", r.status, b)); })
            .catch(e => console.error("[food] Network error:", e));
        }
      }
    } catch(e) { console.error("Failed to save entry", e); }
  };

  if (!session) return <><GoogleFonts /><LoginScreen onAuth={handleAuth} branding={branding} /></>;

  // Route admin to admin dashboard
  if (isAdmin) return (
    <>
      <GoogleFonts />
      <AdminDashboard
        session={session}
        branding={branding}
        onBrandingChange={setBranding}
        onSignOut={() => {
          setSession(null);
          setIsAdmin(false);
          localStorage.removeItem("sf_session");
        }}
      />
    </>
  );

  // Expose setTab + open specific entry from dashboard
  window._sfSetTab = setTab;
  window._sfOpenEntry = (idx) => {
    setOpenEntry(idx);
    setTab("history");
  };

  const todayDateStr = new Date().toISOString().slice(0, 10);
  const todayEntry = entries.find(e => e.entry_date === todayDateStr || new Date(e.created_at).toDateString() === new Date().toDateString());

  return (
    <div style={styles.app}>
      <GoogleFonts />
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${BG}; }
        input[type=range] { -webkit-appearance: none; appearance: none; background: transparent; }
        input[type=range]::-webkit-slider-runnable-track { background: #1a2e34; height: 6px; border-radius: 999px; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; background: ${CORAL}; border-radius: 50%; margin-top: -8px; box-shadow: 0 0 10px rgba(244,97,58,0.5); cursor: pointer; }
        input[type=range]:focus { outline: none; }
        input:focus { border-color: ${TEAL} !important; box-shadow: 0 0 0 2px rgba(0,180,191,0.15) !important; }
        textarea:focus { border-color: ${TEAL} !important; box-shadow: 0 0 0 2px rgba(0,180,191,0.15) !important; }
        @keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }
        @keyframes brainGlow { 0%, 100% { opacity: 0.7; text-shadow: 0 0 4px rgba(244,97,58,0.3); transform: scale(1); } 50% { opacity: 1; text-shadow: 0 0 14px rgba(244,97,58,0.8), 0 0 28px rgba(244,97,58,0.35); transform: scale(1.2); } }
        textarea { font-family: inherit; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: ${BG}; } ::-webkit-scrollbar-thumb { background: ${BORDER}; border-radius: 999px; }
      `}</style>

      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={branding.logoUrl} alt="" style={{ height: 56, width: "auto" }} />
        </div>
        <button onClick={() => { logAudit(session?.user?.id, session?.user?.email, "user_logout", {}, session?.access_token); setSession(null); localStorage.removeItem("sf_session"); }}
          style={{ background: "none", border: "none", color: MUTED, fontSize: 13, cursor: "pointer" }}>
          Sign Out
        </button>
      </header>

      <main>
        {tab === "dashboard" && <DashboardScreen user={userProfile || demoUser} entries={entries} session={session} onFollowUp={handleHistoryFollowUp} branding={branding} />}
        {tab === "checkin" && <CheckInScreen user={userProfile || demoUser} onSubmit={handleCheckInSubmit} onFollowUp={handleFollowUpSubmit} todayEntry={todayEntry} previousEntry={entries[0] || null} branding={branding} />}
        {tab === "history" && <HistoryScreen entries={entries} initialExpanded={openEntry} key={openEntry} onFollowUp={handleHistoryFollowUp} />}
      </main>

      <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0,
        background: `rgba(22,34,40,0.97)`,
        borderTop: `1px solid ${BORDER}`, display: "flex", padding: "8px 16px 20px",
        gap: 8, backdropFilter: "blur(20px)",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.4)" }}>
        {[["dashboard", "📊", "Dashboard"], ["checkin", "✏️", "Check In"], ["history", "📋", "History"]].map(([id, icon, label]) => (
          <button key={id} onClick={() => setTab(id)} style={styles.navBtn(tab === id)}>
            {icon} {label}
          </button>
        ))}
      </nav>
    </div>
  );
}